import { Device } from 'mediasoup-client';
import api from './api';
import { toast } from 'react-toastify';
import { store } from '../store/store';
import { setLocalStream, toggleScreenSharing } from '../store/slices/mediaSlice';
import { isMobileDevice } from './../utils/device';
import meetingRecorder from './meetingRecorder';
import {
  clampMicGain,
  getMeetingAudioConstraintAttempts,
  getMeetingAudioProducerAppData,
  getMeetingOpusCodecOptions,
  getStoredMeetingAudioMode,
  getStoredMicGain,
  normalizeMeetingAudioMode,
  notifyMeetingInputDeviceChange,
  shouldRetryAudioCapture,
  persistMeetingAudioMode,
  prepareMeetingAudioTrack,
  supportsMicrophoneGain,
} from './meetingAudio';
import { getStoredMeetingSessionId } from './meetingAuth';
import {
  classifyMediaApiError,
  classifyMediaOperationError,
} from './mediaReliability';
import {
  chooseAutomaticVideoProfile,
  getStoredVideoQualityMode,
  normalizeVideoQualityMode,
  VIDEO_QUALITY_ORDER,
  VIDEO_QUALITY_PROFILES,
  VIDEO_QUALITY_STORAGE_KEY,
} from './videoQuality';

export default class MediaSoupHandler {
  constructor({ socket, device = null, roomId, userId, sessionId = '' }) {
    this.socket = socket;
    this.roomId = roomId;
    this.userId = userId;
    this.sessionId = String(sessionId || getStoredMeetingSessionId() || '').trim();
    this.device = device?.loaded ? device : null;
    this.sendTransport = null;
    this.recvTransport = null;
    this.localStream = null;
    this.screenStream = null;
    this.screenProducer = null;
    this._screenShareStartPromise = null;
    this._screenShareStopPromise = null;
    this.producers = new Map(); // audio/video
    this.expectedMediaKinds = new Set();
    this.activeConsumers = new Map(); // producerId => consumer
    this.consumePromises = new Map(); // producerId => in-flight consume promise
    this._consumeQueue = Promise.resolve();
    this._recvGeneration = 0;
    this._sendRecoveryPromise = null;
    this._recvRecoveryPromise = null;
    this._recvCreatePromise = null;
    this._sendCreatePromise = null;
    this._legacySendHandover = null;
    this._legacySendRetryTimers = new Map();
    this._signalingReady = false;
    this._signalingRejoinPromise = null;
    this._authenticationRecoveryPromise = null;
    this._sourceJoinStream = null;
    this._mediaEnabled = { audio: false, video: false };
    this._audioToggleGeneration = 0;
    this._trackRecoveryPromises = new Map();
    this._mediaOperationGeneration = { audio: 0, video: 0 };
    this._mediaLifecycleGeneration = 0;
    this._mediaOperationPromises = {
      audio: Promise.resolve(),
      video: Promise.resolve(),
    };
    this._manualDeviceSwitchPending = { audio: 0, video: 0 };
    this._staleProducerIds = new Map();
    this._deviceRecoveryPromise = null;
    this._deviceRecoveryPending = false;
    this._deviceRecoveryPendingDelayMs = 0;
    this._deviceRecoveryTrailingBudget = 0;
    this._deviceChangeTimer = null;
    this._deviceChangeHandler = null;
    this._audioRestoreHandler = null;
    this._trackMuteRecoveryTimers = new Map();
    this._micGain = getStoredMicGain();
    this._audioMode = getStoredMeetingAudioMode();
    this._micInputTrack = null;
    this._micProcessing = null;
    this._micProcessingPromise = null;
    this.mediaRecorder = null;
    this.recordedChunks = [];
    this._lastTx = { bytes: 0, ts: (typeof performance !== 'undefined' ? performance.now() : Date.now()) };
    
    // stats
    this.prevBytesReceived = this.prevBytesReceived || 0;
    this.prevTimestamp = this.prevTimestamp || Date.now();
    this.consumerStatsMap = new Map();

    // Network stats caches
    this._bytesCache = new Map();        // consumer.id -> { ts, bytes }
    this._networkStatsCache = new Map(); // consumer.id -> rolling WebRTC sample
    this._localStatsCache = new Map();   // producer.id -> rolling WebRTC sample
    this._adaptiveStatsCache = null;
    this.producerSessionMap = new Map(); // producerId -> remoteSessionId
    this._adaptTimer = null;
    this._adaptControllerActive = false;
    this._adaptGeneration = 0;
    this._autoUpgradeSamples = 0;
    this._availableOutgoingKbpsEwma = null;
    this._videoQualityMode = getStoredVideoQualityMode();
    this._appliedVideoProfileKey = null;
    this._appliedVideoProducerId = '';
    this._appliedVideoTrackId = '';
    this._videoQualityChangePromise = Promise.resolve();
    
    // local flags
    this._destroyed = false;
    this.showIOSPWAPrompt = this.showIOSPWAPrompt.bind(this);
  }

  isMobileBrowser() {
    return isMobileDevice();
  }

  getMeetingSessionId() {
    return String(
      this.sessionId ||
        getStoredMeetingSessionId() ||
        ''
    ).trim();
  }

  getMeetingMediaPayload(payload = {}) {
    return {
      ...payload,
      userId: this.userId,
      sessionId: this.getMeetingSessionId(),
    };
  }

  getMicGainStatus() {
    return {
      supported: supportsMicrophoneGain(),
      active: Boolean(
        this._micProcessing?.outputTrack?.readyState === 'live'
      ),
      value: this._micGain,
    };
  }

  getAudioModeStatus() {
    const producer = this.producers.get('audio');
    const inputTrack =
      this._micInputTrack || this.localStream?.getAudioTracks?.()[0];
    return {
      mode: this._audioMode,
      active: Boolean(
        producer &&
        !producer.closed &&
        inputTrack?.readyState === 'live'
      ),
      pending: !inputTrack || inputTrack.readyState !== 'live',
      headphonesRecommended: this._audioMode === 'worship',
    };
  }

  _emitAudioModeState(extra = {}) {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
      new CustomEvent('meeting-audio-mode-change', {
        detail: {
          ...this.getAudioModeStatus(),
          ...extra,
        },
      })
    );
  }

  async _applyAudioProducerMode() {
    const producer = this.producers.get('audio');
    if (!producer || producer.closed) return;
    const maxBitrate =
      this._audioMode === 'worship' ? 128_000 : 40_000;
    const sender = producer.rtpSender;
    if (sender?.getParameters && sender?.setParameters) {
      const parameters = sender.getParameters();
      const encodings = Array.isArray(parameters?.encodings)
        ? parameters.encodings
        : [];
      if (encodings.length) {
        parameters.encodings = encodings.map((encoding) => ({
          ...encoding,
          maxBitrate,
        }));
        await sender.setParameters(parameters);
        return;
      }
    }
    if (producer.setRtpEncodingParameters) {
      await producer.setRtpEncodingParameters({ maxBitrate });
    }
  }

  _applyAudioConsumerLatencyHint(consumer, forceWorship = false) {
    if (!consumer || consumer.kind !== 'audio') return;
    const receiver = consumer.rtpReceiver;
    if (!receiver) return;
    const lowLatency = forceWorship || this._audioMode === 'worship';
    try {
      if ('playoutDelayHint' in receiver) {
        receiver.playoutDelayHint = lowLatency ? 0 : null;
      }
    } catch {}
    try {
      if ('jitterBufferTarget' in receiver) {
        receiver.jitterBufferTarget = lowLatency ? 0 : null;
      }
    } catch {}
  }

  _applyAudioConsumerLatencyHints() {
    this.activeConsumers.forEach((consumer) => {
      const appData = consumer?.appData || {};
      this._applyAudioConsumerLatencyHint(
        consumer,
        appData.audioMode === 'worship' || appData.contentType === 'music'
      );
    });
  }

  setAudioMode(value) {
    const nextMode = normalizeMeetingAudioMode(value);
    return this._queueMediaOperation('audio', async (assertCurrent) => {
      const previousMode = this._audioMode;
      if (nextMode === previousMode) {
        this._emitAudioModeState();
        return this.getAudioModeStatus();
      }

      const hadLiveInput = Boolean(
        (this._micInputTrack || this.localStream?.getAudioTracks?.()[0])
          ?.readyState === 'live'
      );
      this._audioMode = nextMode;
      try {
        if (hadLiveInput) {
          await this._reacquireAudioTrack();
          assertCurrent();
          await this._applyAudioProducerMode();
          assertCurrent();
        }
        persistMeetingAudioMode(nextMode);
        this._applyAudioConsumerLatencyHints();
        this._emitAudioModeState();
        return this.getAudioModeStatus();
      } catch (error) {
        this._audioMode = previousMode;
        if (hadLiveInput && error?.code !== 'STALE_MEDIA_OPERATION') {
          try {
            await this._reacquireAudioTrack();
            await this._applyAudioProducerMode();
          } catch (rollbackError) {
            console.warn(
              'Previous microphone mode could not be fully restored:',
              rollbackError?.message || rollbackError
            );
          }
        }
        this._applyAudioConsumerLatencyHints();
        this._emitAudioModeState({
          error:
            error?.message ||
            'The microphone could not switch audio modes.',
        });
        throw error;
      }
    });
  }

  _setMicGainNodeValue(value) {
    const processing = this._micProcessing;
    if (!processing?.gain) return;
    const now = processing.context.currentTime;
    processing.gain.gain.cancelScheduledValues(now);
    processing.gain.gain.setTargetAtTime(value, now, 0.015);
  }

  async _buildMicProcessing(rawTrack) {
    if (!rawTrack || rawTrack.readyState !== 'live') {
      throw new Error('Microphone is not active.');
    }
    if (!supportsMicrophoneGain()) {
      const error = new Error(
        'Microphone volume control is not supported by this browser.'
      );
      error.code = 'MIC_GAIN_UNSUPPORTED';
      throw error;
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    let context = null;
    try {
      context = new AudioContextClass({ latencyHint: 'interactive' });
    } catch {
      context = new AudioContextClass();
    }

    try {
      await context.resume?.();
      if (context.state !== 'running') {
        const error = new Error(
          'Tap the meeting once, then adjust microphone volume again.'
        );
        error.code = 'MIC_GAIN_GESTURE_REQUIRED';
        throw error;
      }

      const source = context.createMediaStreamSource(
        new MediaStream([rawTrack])
      );
      const gain = context.createGain();
      const limiter = context.createDynamicsCompressor();
      const destination = context.createMediaStreamDestination();
      gain.gain.value = this._micGain;
      // Prevent clipping when a user boosts a quiet Bluetooth microphone.
      limiter.threshold.value = -3;
      limiter.knee.value = 2;
      limiter.ratio.value = 12;
      limiter.attack.value = 0.003;
      limiter.release.value = 0.2;
      source.connect(gain);
      gain.connect(limiter);
      limiter.connect(destination);

      const outputTrack = destination.stream.getAudioTracks()[0];
      if (!outputTrack) {
        throw new Error('Unable to create an adjustable microphone track.');
      }
      try {
        outputTrack.contentHint =
          this._audioMode === 'worship' ? 'music' : 'speech';
      } catch {}

      return {
        context,
        source,
        gain,
        limiter,
        destination,
        outputTrack,
      };
    } catch (error) {
      try { await context.close?.(); } catch {}
      throw error;
    }
  }

  async _createMicProcessing(rawTrack) {
    const processing = await this._buildMicProcessing(rawTrack);
    this._micProcessing = processing;
    this._micInputTrack = rawTrack;
    return processing.outputTrack;
  }

  async _stageAudioInput(rawTrack) {
    const existing = this._micProcessing;
    const naturalCapture = (gainUnavailable = false) => ({
      outgoingTrack: rawTrack,
      commit: () => {
        // Dispose the old graph only after replaceTrack succeeds. A browser
        // interruption must not leave the sender publishing a silent output.
        if (existing) this._disposeMicProcessing({ stopInput: false });
        this._micInputTrack = rawTrack;
        if (gainUnavailable) this._emitAudioModeState({ gainUnavailable: true });
      },
      rollback: () => undefined,
    });
    if (
      existing?.outputTrack?.readyState === 'live'
    ) {
      try {
        existing.outputTrack.contentHint =
          this._audioMode === 'worship' ? 'music' : 'speech';
      } catch {}
      try { await existing.context.resume?.(); } catch {}
      if (existing.context.state !== 'running') return naturalCapture(true);
      const nextSource = existing.context.createMediaStreamSource(
        new MediaStream([rawTrack])
      );
      const previousSource = existing.source;
      let committed = false;
      return {
        outgoingTrack: existing.outputTrack,
        commit: () => {
          nextSource.connect(existing.gain);
          try { existing.source?.disconnect(); } catch {}
          existing.source = nextSource;
          this._micInputTrack = rawTrack;
          this._setMicGainNodeValue(this._micGain);
          committed = true;
        },
        rollback: () => {
          if (committed) {
            try { previousSource?.connect(existing.gain); } catch {}
            try { nextSource.disconnect(); } catch {}
            existing.source = previousSource;
          } else {
            try { nextSource.disconnect(); } catch {}
          }
        },
      };
    }

    if (Math.abs(this._micGain - 1) >= 0.001) {
      let processing;
      try { processing = await this._buildMicProcessing(rawTrack); }
      catch (error) {
        console.warn('Microphone gain is unavailable; keeping natural capture:', error?.message || error);
        return naturalCapture(true);
      }
      let committed = false;
      return {
        outgoingTrack: processing.outputTrack,
        commit: () => {
          this._micProcessing = processing;
          this._micInputTrack = rawTrack;
          committed = true;
        },
        rollback: () => {
          if (committed && this._micProcessing === processing) {
            this._micProcessing = null;
          }
          try { processing.source?.disconnect(); } catch {}
          try { processing.gain?.disconnect(); } catch {}
          try { processing.limiter?.disconnect(); } catch {}
          try { processing.outputTrack?.stop(); } catch {}
          try { processing.context?.close?.(); } catch {}
        },
      };
    }

    return naturalCapture();
  }

  async _replaceMicProcessingInput(rawTrack) {
    const processing = this._micProcessing;
    if (!processing?.outputTrack || processing.outputTrack.readyState !== 'live') {
      return this._createMicProcessing(rawTrack);
    }
    await processing.context.resume?.();
    if (processing.context.state !== 'running') {
      throw new Error(
        'Tap the meeting once, then choose the microphone again.'
      );
    }

    const nextSource = processing.context.createMediaStreamSource(
      new MediaStream([rawTrack])
    );
    nextSource.connect(processing.gain);
    try { processing.source?.disconnect(); } catch {}
    processing.source = nextSource;
    this._micInputTrack = rawTrack;
    this._setMicGainNodeValue(this._micGain);
    return processing.outputTrack;
  }

  _disposeMicProcessing({ stopInput = true } = {}) {
    const processing = this._micProcessing;
    if (processing) {
      try { processing.source?.disconnect(); } catch {}
      try { processing.gain?.disconnect(); } catch {}
      try { processing.limiter?.disconnect(); } catch {}
      try {
        if (processing.outputTrack) processing.outputTrack.onended = null;
        processing.outputTrack?.stop();
      } catch {}
      try { processing.context?.close?.(); } catch {}
    }
    if (stopInput) {
      try {
        this._detachLocalTrackHealth(this._micInputTrack, 'audio');
        this._micInputTrack?.stop();
      } catch {}
    }
    this._micProcessing = null;
    this._micInputTrack = null;
    this._micProcessingPromise = null;
  }

  /**
   * Adds adjustable gain after browser echo cancellation and before Opus.
   * The processed destination track stays stable after it is first enabled, so
   * later slider/input-device changes do not renegotiate the mediasoup sender.
   */
  setMicGain(value) {
    const nextGain = clampMicGain(value);
    this._micGain = nextGain;
    localStorage.setItem('meeting.micGain', String(nextGain));
    return this._queueMediaOperation('audio', (assertCurrent) => {
      // Slider changes can arrive faster than Web Audio starts. Skip obsolete
      // queued values and apply only the latest user choice.
      if (nextGain !== this._micGain) return this.getMicGainStatus();
      return this._setMicGainInternal(nextGain, assertCurrent);
    });
  }

  async _setMicGainInternal(nextGain, assertCurrent = () => undefined) {
    if (this._micProcessing?.outputTrack?.readyState === 'live') {
      this._setMicGainNodeValue(nextGain);
      return this.getMicGainStatus();
    }
    // At 100%, keep the native capture track. This preserves the safest
    // autoplay behavior until the user actually asks for volume adjustment.
    if (Math.abs(nextGain - 1) < 0.001) return this.getMicGainStatus();

    const rawTrack =
      this._micInputTrack ||
      this.localStream?.getAudioTracks?.()[0];
    if (!rawTrack || rawTrack.readyState !== 'live') {
      throw new Error('Turn on the microphone before adjusting its volume.');
    }

    if (!this._micProcessingPromise) {
      this._micProcessingPromise = (async () => {
        try {
          const outgoingTrack = await this._createMicProcessing(rawTrack);
          assertCurrent();
          const previousOutgoing = this.localStream?.getAudioTracks?.()[0];
          outgoingTrack.enabled = previousOutgoing?.enabled !== false;

          const producer = this.producers.get('audio');
          if (
            producer &&
            !producer.closed &&
            producer.track?.id !== outgoingTrack.id
          ) {
            await producer.replaceTrack({ track: outgoingTrack });
            assertCurrent();
          }
          this.localStream = new MediaStream([
            ...(this.localStream?.getVideoTracks?.() || []),
            outgoingTrack,
          ]);
          store.dispatch(setLocalStream(this.localStream));
          return outgoingTrack;
        } catch (error) {
          this._disposeMicProcessing({ stopInput: false });
          if (error?.code !== 'STALE_MEDIA_OPERATION') {
            this._micInputTrack = rawTrack;
          }
          throw error;
        }
      })().finally(() => {
        this._micProcessingPromise = null;
      });
    }

    await this._micProcessingPromise;
    this._setMicGainNodeValue(this._micGain);
    return this.getMicGainStatus();
  }

  setSignalingReady(ready) {
    this._signalingReady = Boolean(ready);
  }

  _handleSignalingApiError(error, detail = {}) {
    const classification = classifyMediaApiError(error);
    if (
      classification === 'authentication' &&
      !this._authenticationRecoveryPromise &&
      typeof this.onAuthenticationRequired === 'function'
    ) {
      const recovery = Promise.resolve()
        .then(() => this.onAuthenticationRequired({ error, ...detail }))
        .then((result) => result !== false)
        .catch((authenticationError) => {
          console.warn(
            'Meeting media authentication recovery failed:',
            authenticationError?.message || authenticationError
          );
          return false;
        })
        .finally(() => {
          if (this._authenticationRecoveryPromise === recovery) {
            this._authenticationRecoveryPromise = null;
          }
        });
      this._authenticationRecoveryPromise = recovery;
      return classification;
    }
    if (classification !== 'rejoin') return classification;

    this.setSignalingReady(false);
    if (
      !this._signalingRejoinPromise &&
      typeof this.onSignalingRejoinRequired === 'function'
    ) {
      const rejoin = Promise.resolve().then(() =>
        this.onSignalingRejoinRequired({ error, ...detail })
      );
      let rejoinPromise;
      rejoinPromise = rejoin
        .catch((rejoinError) => {
          console.warn(
            'Media session rejoin failed:',
            rejoinError?.message || rejoinError
          );
        })
        .finally(() => {
          if (this._signalingRejoinPromise === rejoinPromise) {
            this._signalingRejoinPromise = null;
          }
        });
      this._signalingRejoinPromise = rejoinPromise;
    }
    return classification;
  }

  async waitForAuthenticationRecovery() {
    const recovery = this._authenticationRecoveryPromise;
    if (!recovery) return false;
    return recovery;
  }

  isSignalingReady() {
    return (
      !this._destroyed &&
      this._signalingReady &&
      this.socket?.connected !== false
    );
  }

  getLocalMediaState(kind) {
    const cleanKind = String(kind || '').toLowerCase();
    const track = this.localStream?.getTracks?.().find(
      (item) => item.kind === cleanKind
    );
    const producer = this.producers.get(cleanKind);
    return {
      kind: cleanKind,
      enabled: this._mediaEnabled[cleanKind] === true,
      available: Boolean(track && track.readyState === 'live'),
      producerActive: Boolean(producer && !producer.closed && !producer.paused),
    };
  }

  _notifyMicrophoneInput(reason = 'device-selected') {
    const track = this._micInputTrack;
    if (!track || track.readyState !== 'live') return;
    notifyMeetingInputDeviceChange({
      deviceId: track.getSettings?.().deviceId || '',
      trackId: track.id,
      enabled: this._mediaEnabled.audio === true,
      reason,
    });
  }

  async _ensureMicrophoneProcessingRunning(assertCurrent = () => undefined) {
    const processing = this._micProcessing;
    if (!processing) return;
    try { await processing.context.resume?.(); } catch {}
    assertCurrent();
    if (processing.context.state === 'running' && processing.outputTrack?.readyState === 'live') return;
    const rawTrack = this._micInputTrack;
    if (!rawTrack || rawTrack.readyState !== 'live') return;
    const enabled = this._mediaEnabled.audio === true;
    rawTrack.enabled = enabled;
    const producer = this.producers.get('audio');
    if (producer && !producer.closed && producer.track?.id !== rawTrack.id) {
      await producer.replaceTrack({ track: rawTrack });
      assertCurrent();
    }
    this._disposeMicProcessing({ stopInput: false });
    this._micInputTrack = rawTrack;
    this._attachLocalTrackHealth(rawTrack, 'audio');
    this.localStream = new MediaStream([
      ...(this.localStream?.getVideoTracks?.() || []), rawTrack,
    ]);
    store.dispatch(setLocalStream(this.localStream));
    this._emitAudioModeState({ gainUnavailable: true });
    this._notifyMicrophoneInput('natural-capture-recovered');
  }

  _notifyLocalMediaState(kind, detail = {}) {
    const state = {
      ...this.getLocalMediaState(kind),
      ...detail,
    };
    this.onLocalMediaStateChange?.(state);
    return state;
  }

  _pruneStaleProducerIds() {
    const now = Date.now();
    for (const [producerId, expiresAt] of this._staleProducerIds.entries()) {
      if (expiresAt <= now) this._staleProducerIds.delete(producerId);
    }
    if (this._staleProducerIds.size <= 500) return;
    const oldest = [...this._staleProducerIds.entries()]
      .sort((left, right) => left[1] - right[1])
      .slice(0, this._staleProducerIds.size - 500);
    oldest.forEach(([producerId]) => this._staleProducerIds.delete(producerId));
  }

  markProducerStale(producerId, ttlMs = 120000) {
    const cleanProducerId = String(producerId || '').trim();
    if (!cleanProducerId) return;
    this._pruneStaleProducerIds();
    this._staleProducerIds.set(cleanProducerId, Date.now() + ttlMs);
  }

  clearProducerStale(producerId) {
    this._staleProducerIds.delete(String(producerId || '').trim());
  }

  isProducerStale(producerId) {
    this._pruneStaleProducerIds();
    return (
      Number(this._staleProducerIds.get(String(producerId || '').trim()) || 0) >
      Date.now()
    );
  }

  async waitForSignalingReady(timeoutMs = 30000) {
    const startedAt = Date.now();
    while (!this._destroyed && !this._signalingReady) {
      if (Date.now() - startedAt >= timeoutMs) {
        const error = new Error('Meeting signalling did not reconnect in time');
        error.code = 'SIGNALLING_TIMEOUT';
        throw error;
      }
      await new Promise((resolve) => window.setTimeout(resolve, 250));
    }
    if (this._destroyed) {
      const error = new Error(
        'Meeting closed while waiting for signalling.'
      );
      error.code = 'STALE_MEDIA_OPERATION';
      throw error;
    }
  }

  startDeviceMonitor() {
    if (this._audioRestoreHandler) return;
    this._audioRestoreHandler = () => {
      if (this._destroyed || this._mediaEnabled.audio !== true ||
          document.visibilityState === 'hidden') return;
      void this._queueMediaOperation('audio', (assertCurrent) =>
        this._ensureLocalMediaKind('audio', assertCurrent)
      ).catch((error) => {
        if (error?.code !== 'STALE_MEDIA_OPERATION') {
          console.warn('Microphone restore is still retrying:', error?.message || error);
        }
      });
    };
    window.addEventListener('pointerdown', this._audioRestoreHandler, { passive: true });
    window.addEventListener('focus', this._audioRestoreHandler);
    window.addEventListener('pageshow', this._audioRestoreHandler);
    document.addEventListener('visibilitychange', this._audioRestoreHandler);
    // Older mobile browsers can resume audio after a gesture even when they
    // do not expose the devicechange event.
    if (!navigator.mediaDevices?.addEventListener) return;
    this._deviceChangeHandler = () => {
      // Bluetooth profile changes often expose an empty device list between
      // A2DP and HFP. Always reserve one delayed enumeration after the burst.
      this._deviceRecoveryTrailingBudget = 1;
      this._scheduleDeviceRecovery();
    };
    navigator.mediaDevices.addEventListener(
      'devicechange',
      this._deviceChangeHandler
    );
  }

  _scheduleDeviceRecovery(delayMs = 400) {
    if (this._destroyed) return;
    this._deviceRecoveryPending = true;
    this._deviceRecoveryPendingDelayMs = Math.max(
      this._deviceRecoveryPendingDelayMs,
      Number(delayMs) || 400
    );
    if (this._deviceChangeTimer) {
      window.clearTimeout(this._deviceChangeTimer);
    }
    this._deviceChangeTimer = window.setTimeout(() => {
      this._deviceChangeTimer = null;
      void this._runDeviceRecovery();
    }, delayMs);
  }

  async _runDeviceRecovery() {
    if (this._destroyed) return false;
    if (this._deviceRecoveryPromise) {
      // A Bluetooth A2DP/HFP transition emits several events. Never discard
      // the last one while an earlier enumeration is still resolving.
      this._deviceRecoveryPending = true;
      this._deviceRecoveryPendingDelayMs = Math.max(
        this._deviceRecoveryPendingDelayMs,
        400
      );
      return this._deviceRecoveryPromise;
    }
    this._deviceRecoveryPending = false;
    this._deviceRecoveryPendingDelayMs = 0;
    const recovery = this.recoverMissingDevices()
      .catch((error) => {
        console.warn('Meeting device fallback failed:', error?.message || error);
        return false;
      })
      .finally(() => {
        if (this._deviceRecoveryPromise === recovery) {
          this._deviceRecoveryPromise = null;
        }
        if (this._deviceRecoveryPending && !this._destroyed) {
          const pendingDelay =
            this._deviceRecoveryPendingDelayMs || 400;
          this._deviceRecoveryPending = false;
          this._deviceRecoveryPendingDelayMs = 0;
          this._scheduleDeviceRecovery(pendingDelay);
        }
      });
    this._deviceRecoveryPromise = recovery;
    return recovery;
  }

  stopDeviceMonitor() {
    if (this._audioRestoreHandler) {
      window.removeEventListener('pointerdown', this._audioRestoreHandler);
      window.removeEventListener('focus', this._audioRestoreHandler);
      window.removeEventListener('pageshow', this._audioRestoreHandler);
      document.removeEventListener('visibilitychange', this._audioRestoreHandler);
      this._audioRestoreHandler = null;
    }
    if (this._deviceChangeTimer) {
      window.clearTimeout(this._deviceChangeTimer);
      this._deviceChangeTimer = null;
    }
    this._deviceRecoveryPending = false;
    this._deviceRecoveryPendingDelayMs = 0;
    this._deviceRecoveryTrailingBudget = 0;
    if (this._deviceChangeHandler) {
      navigator.mediaDevices?.removeEventListener?.(
        'devicechange',
        this._deviceChangeHandler
      );
    }
    this._deviceChangeHandler = null;
  }

  async recoverMissingDevices() {
    if (!navigator.mediaDevices?.enumerateDevices) return false;
    const devices = await navigator.mediaDevices.enumerateDevices();
    const hasDevice = (kind, deviceId) =>
      !deviceId ||
      devices.some(
        (item) => item.kind === kind && String(item.deviceId) === String(deviceId)
      );
    const audioInputId = localStorage.getItem('meeting.audioInputId') || '';
    const videoInputId = localStorage.getItem('meeting.videoInputId') || '';
    const audioOutputId = localStorage.getItem('meeting.audioOutputId') || '';
    const preferredDeviceTemporarilyMissing = Boolean(
      (audioInputId && !hasDevice('audioinput', audioInputId)) ||
      (videoInputId && !hasDevice('videoinput', videoInputId)) ||
      (audioOutputId && !hasDevice('audiooutput', audioOutputId))
    );

    if (audioOutputId && !hasDevice('audiooutput', audioOutputId)) {
      // enumerateDevices can temporarily hide a permitted output while Linux
      // changes Bluetooth profiles or when speaker permission is not exposed.
      // Keep the preference so reconnect can restore it; use default only for
      // current playback.
      window.dispatchEvent(
        new CustomEvent('meeting-output-device-change', {
          detail: {
            deviceId: '',
            preferredDeviceId: audioOutputId,
            reason: 'preferred-output-unavailable',
          },
        })
      );
    } else if (audioOutputId) {
      window.dispatchEvent(
        new CustomEvent('meeting-output-device-change', {
          detail: {
            deviceId: audioOutputId,
            reason: 'preferred-output-available',
          },
        })
      );
    }

    if (
      audioInputId &&
      hasDevice('audioinput', audioInputId) &&
      this._manualDeviceSwitchPending.audio === 0
    ) {
      const currentAudioDeviceId = String(
        this._micInputTrack?.getSettings?.().deviceId || ''
      );
      if (
        currentAudioDeviceId &&
        currentAudioDeviceId !== audioInputId
      ) {
        await this._queueMediaOperation(
          'audio',
          () => {
            if (
              this._manualDeviceSwitchPending.audio > 0 ||
              localStorage.getItem('meeting.audioInputId') !== audioInputId
            ) {
              return false;
            }
            return this._switchInputDevice(
              'audio',
              audioInputId,
              { persist: false }
            );
          }
        ).catch((error) => {
          console.warn(
            'Preferred microphone is not ready yet:',
            error?.message || error
          );
          if (this._deviceRecoveryTrailingBudget > 0) {
            this._deviceRecoveryTrailingBudget -= 1;
            this._deviceRecoveryPending = true;
            this._deviceRecoveryPendingDelayMs = Math.max(
              this._deviceRecoveryPendingDelayMs,
              1200
            );
          }
        });
      }
    } else if (!hasDevice('audioinput', audioInputId)) {
      const inputUnavailable =
        this._mediaEnabled.audio === true &&
        (!this._micInputTrack ||
          this._micInputTrack.readyState !== 'live' ||
          this._micInputTrack.muted === true);
      if (inputUnavailable) {
        await this.handleTrackEnded('audio');
      }
    }

    if (
      videoInputId &&
      hasDevice('videoinput', videoInputId) &&
      this._manualDeviceSwitchPending.video === 0
    ) {
      const currentVideoDeviceId = String(
        this.localStream
          ?.getVideoTracks?.()[0]
          ?.getSettings?.().deviceId || ''
      );
      if (
        currentVideoDeviceId &&
        currentVideoDeviceId !== videoInputId
      ) {
        await this._queueMediaOperation(
          'video',
          () => {
            if (
              this._manualDeviceSwitchPending.video > 0 ||
              localStorage.getItem('meeting.videoInputId') !== videoInputId
            ) {
              return false;
            }
            return this._switchInputDevice(
              'video',
              videoInputId,
              { persist: false }
            );
          }
        ).catch((error) => {
          console.warn(
            'Preferred camera is not ready yet:',
            error?.message || error
          );
          if (this._deviceRecoveryTrailingBudget > 0) {
            this._deviceRecoveryTrailingBudget -= 1;
            this._deviceRecoveryPending = true;
            this._deviceRecoveryPendingDelayMs = Math.max(
              this._deviceRecoveryPendingDelayMs,
              1200
            );
          }
        });
      }
    } else if (!hasDevice('videoinput', videoInputId)) {
      const currentVideoTrack = this.localStream?.getVideoTracks?.()[0];
      const inputUnavailable =
        this._mediaEnabled.video === true &&
        (!currentVideoTrack || currentVideoTrack.readyState !== 'live');
      if (inputUnavailable) {
        await this.handleTrackEnded('video');
      }
    }
    if (
      preferredDeviceTemporarilyMissing &&
      this._deviceRecoveryTrailingBudget > 0 &&
      !this._destroyed
    ) {
      this._deviceRecoveryTrailingBudget -= 1;
      this._deviceRecoveryPending = true;
      this._deviceRecoveryPendingDelayMs = Math.max(
        this._deviceRecoveryPendingDelayMs,
        1200
      );
    }
    return true;
  }

  _clearTrackMuteRecovery(kind, track = null) {
    const pending = this._trackMuteRecoveryTimers.get(kind);
    if (!pending || (track && pending.track !== track)) return;
    window.clearTimeout(pending.timer);
    this._trackMuteRecoveryTimers.delete(kind);
  }

  _detachLocalTrackHealth(track, kind) {
    if (!track) return;
    this._clearTrackMuteRecovery(kind, track);
    try {
      track.onended = null;
      track.onmute = null;
      track.onunmute = null;
    } catch {}
  }

  _attachLocalTrackHealth(track, kind) {
    if (!track) return;
    this._detachLocalTrackHealth(track, kind);
    track.onended = () => {
      this._clearTrackMuteRecovery(kind, track);
      void this.handleTrackEnded(kind);
    };
    if (kind !== 'audio') return;
    track.onmute = () => {
      this._clearTrackMuteRecovery(kind);
      const timer = window.setTimeout(() => {
        this._trackMuteRecoveryTimers.delete(kind);
        if (
          this._destroyed ||
          this._mediaEnabled.audio !== true ||
          this._micInputTrack !== track ||
          track.readyState !== 'live' ||
          track.muted !== true
        ) {
          return;
        }
        void this.handleTrackEnded('audio');
      }, 1500);
      this._trackMuteRecoveryTimers.set(kind, { timer, track });
    };
    track.onunmute = () => this._clearTrackMuteRecovery(kind, track);
  }

  queueConsume(task) {
    const queued = this._consumeQueue.then(task, task);
    this._consumeQueue = queued.catch(() => undefined);
    return queued;
  }

  async closeServerConsumer(consumerId) {
    if (!consumerId) return;
    await api.delete(
      `/mediasoup/${this.roomId}/consumers/${consumerId}`,
      { data: this.getMeetingMediaPayload() }
    ).catch((error) => {
      if (![404, 410].includes(error?.response?.status)) throw error;
    });
  }

  async closeServerTransport(transportId) {
    if (!transportId) return;
    await api.delete(
      `/mediasoup/${this.roomId}/transports/${transportId}`,
      { data: this.getMeetingMediaPayload() }
    ).catch((error) => {
      if (![404, 410].includes(error?.response?.status)) throw error;
    });
  }

  async restartTransportIce(transport) {
    if (!transport || transport.closed) return false;
    await this.waitForSignalingReady();
    try {
      const { data } = await api.post(
        `/mediasoup/${this.roomId}/transports/${transport.id}/restart-ice`,
        this.getMeetingMediaPayload()
      );
      if (!data?.iceParameters) return false;
      await transport.restartIce({ iceParameters: data.iceParameters });
      return true;
    } catch (error) {
      if (![404, 410].includes(error?.response?.status)) {
        console.warn('ICE restart failed; rebuilding transport:', error?.message || error);
      }
      return false;
    }
  }

  async initializeDevice(routerRtpCapabilities) {
    if (this.device && this.device.loaded) {
      console.warn('Device already initialized. Skipping load().');
      return;
    }
    this.device = new Device();
    await this.device.load({ routerRtpCapabilities });
    console.log('Mediasoup Device loaded successfully.');
  }

  async joinRoom({ stream }) {
    if (this._destroyed) return;
    if (!stream) throw new Error('Stream is null in joinRoom()');
    const lifecycleGeneration = this._mediaLifecycleGeneration;
    const assertJoinCurrent = () => {
      if (
        this._destroyed ||
        lifecycleGeneration !== this._mediaLifecycleGeneration
      ) {
        const error = new Error(
          'Meeting closed while local media was joining.'
        );
        error.code = 'STALE_MEDIA_OPERATION';
        throw error;
      }
    };

    const sameStream =
      this.localStream === stream ||
      this._sourceJoinStream === stream;
    const hasLiveProduction = Array.from(this.producers.values()).some(
      (producer) => producer && !producer.closed
    );
    if (sameStream && hasLiveProduction) return;

    if (!sameStream && this.localStream) {
      this.localStream.getTracks().forEach((track) => {
        track.onended = null;
        track.stop();
      });
      this._disposeMicProcessing();
      this.producers.forEach((producer) => {
        try { producer.close(); } catch {}
      });
      this.producers.clear();
    }
    this._sourceJoinStream = stream;
    this.localStream = stream;
    for (const kind of ['audio', 'video']) {
      const track = stream.getTracks().find((item) => item.kind === kind);
      this._mediaEnabled[kind] = Boolean(
        track && track.readyState === 'live' && track.enabled !== false
      );
      if (track) this.expectedMediaKinds.add(kind);
    }
    try {
        // A send transport only begins DTLS negotiation when the first track is
        // produced. Creating one for an empty pre-join stream leaves it in the
        // `new` state, so the server's orphan cleanup correctly removes it
        // while mediasoup-client still considers the local object open. Create
        // it lazily when there is actually something to publish instead.
        const hasLiveLocalTrack = stream.getTracks().some(
          (track) => track?.readyState === 'live'
        );
        if (
          hasLiveLocalTrack &&
          (!this.sendTransport || this.sendTransport.closed)
        ) {
            await this.createSendTransport();
            assertJoinCurrent();
        }
        if (!this.recvTransport || this.recvTransport.closed) {
            await this.createRecvTransport();
            assertJoinCurrent();
        }

        // VIDEO
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack && videoTrack.readyState === 'live') {
            const videoProducer = await this.produceTrack({
              track: videoTrack,
              kind: 'video',
            });
            assertJoinCurrent();
            if (this._mediaEnabled.video !== true && !videoProducer.paused) {
              await videoProducer.pause();
              assertJoinCurrent();
            }
        }

        // AUDIO
        const rawAudioTrack = stream.getAudioTracks()[0];
        if (rawAudioTrack && rawAudioTrack.readyState === 'live') {
            await prepareMeetingAudioTrack(rawAudioTrack, this._audioMode);
            assertJoinCurrent();
            this._micInputTrack = rawAudioTrack;
            this._attachLocalTrackHealth(rawAudioTrack, 'audio');
            let outgoingAudioTrack = rawAudioTrack;
            if (Math.abs(this._micGain - 1) >= 0.001) {
              try {
                outgoingAudioTrack = await this._createMicProcessing(rawAudioTrack);
                assertJoinCurrent();
                this.localStream = new MediaStream([
                  ...stream.getVideoTracks(),
                  outgoingAudioTrack,
                ]);
                store.dispatch(setLocalStream(this.localStream));
              } catch (error) {
                if (error?.code === 'STALE_MEDIA_OPERATION') {
                  this._disposeMicProcessing({ stopInput: false });
                  throw error;
                }
                // Never make the microphone silent because a persisted gain
                // setting cannot start Web Audio before the first gesture.
                console.warn(
                  'Saved microphone volume could not be applied; using native capture:',
                  error?.message || error
                );
                outgoingAudioTrack = rawAudioTrack;
              }
            }
            const audioProducer = await this.produceTrack({
                track: outgoingAudioTrack,
                kind: 'audio',
                appData: getMeetingAudioProducerAppData(this._audioMode, {
                  processed: outgoingAudioTrack !== rawAudioTrack,
                })
            });
            assertJoinCurrent();
            if (this._mediaEnabled.audio !== true && !audioProducer.paused) {
              await audioProducer.pause();
              assertJoinCurrent();
            }
        }
        assertJoinCurrent();
        this._notifyMicrophoneInput('joined');
        this.startDeviceMonitor();
    } catch (error) {
        if (error?.code === 'STALE_MEDIA_OPERATION') {
          this._disposeMicProcessing({ stopInput: false });
        }
        console.error('Error joining room:', error);
        throw error;
    }
  }

  async produceTrack({ track, kind, appData = {} }) {
    const lifecycleGeneration = this._mediaLifecycleGeneration;
    const assertProductionCurrent = () => {
      if (
        this._destroyed ||
        lifecycleGeneration !== this._mediaLifecycleGeneration
      ) {
        const error = new Error(
          'Meeting media changed while the track was being published.'
        );
        error.code = 'STALE_MEDIA_OPERATION';
        throw error;
      }
    };
    let producer = null;
    try {
      assertProductionCurrent();
      if (!track || track.readyState !== 'live') {
        throw new Error(`Cannot produce ${kind} track - track not available or not live`);
      }

      const transport = await this.getOrCreateSendTransport();

      // Camera uses three spatial simulcast layers. Screen sharing uses one
      // high-quality layer. Do not force scalabilityMode here: Chrome rejects
      // S1T2/S1T3 when the negotiated codec/profile (commonly H264 on some
      // devices) does not expose temporal layers, causing addTransceiver() to
      // fail before the screen producer is created.
      const isVideo = kind === 'video';
      const mediaType = appData?.type === 'screen' ? 'screen' : kind;
      const encodings = isVideo
        ? mediaType === 'screen'
          ? [{ maxBitrate: 1_500_000 }]
          : [
            // Keep a very small base layer available so cameras remain visible
            // on congested 2G/3G and mobile-hotspot connections.
            { maxBitrate: 120_000, scaleResolutionDownBy: 4 },
            { maxBitrate: 380_000, scaleResolutionDownBy: 2 },
            { maxBitrate: 1_700_000, scaleResolutionDownBy: 1 },
          ]
        : undefined;

      // Screen share hint
      if (isVideo && (track.label?.toLowerCase()?.includes('screen') || appData?.type === 'screen')) {
        try { track.contentHint = 'detail'; } catch {}
      }

      producer = await transport.produce({
        track,
        encodings,
        appData: {
          ...appData,
          type: mediaType,
          mediaType,
        },
        codecOptions:
          kind === 'audio'
            ? getMeetingOpusCodecOptions(this._audioMode)
            : undefined
      });
      assertProductionCurrent();

      if (mediaType === 'screen') this.screenProducer = producer;
      else this.producers.set(kind, producer);
      void this._retireMigratedLegacyProducer(mediaType, producer);

      if (mediaType === 'video') {
        // Apply a saved/manual profile to every new or recovered camera
        // producer. This changes capture/sender parameters in place and never
        // rebuilds the transport.
        await this._queueVideoQualityOperation(async () => {
          const target =
            this._videoQualityMode === 'auto'
              ? this._appliedVideoProfileKey || 'medium'
              : this._videoQualityMode;
          try {
            await this._applyCameraVideoProfile(target);
          } catch (qualityError) {
            console.warn(
              'Saved camera quality could not be applied; using browser defaults:',
              qualityError?.message || qualityError
            );
          }
          if (this._videoQualityMode === 'auto') {
            this.startAdaptiveController();
          }
        });
      }
      assertProductionCurrent();

      track.onended = () => {
        if (this._destroyed || this._mediaEnabled[kind] !== true) return;
        console.warn(`${kind} track ended, attempting to recover...`);
        void this.handleTrackEnded(kind);
      };

      return producer;
    } catch (error) {
      if (
        producer &&
        (
          error?.code === 'STALE_MEDIA_OPERATION' ||
          this._destroyed ||
          lifecycleGeneration !== this._mediaLifecycleGeneration
        )
      ) {
        if (this.producers.get(kind) === producer) {
          this.producers.delete(kind);
        }
        if (this.screenProducer === producer) this.screenProducer = null;
        if (producer.id) {
          await api.delete(
            `/mediasoup/${this.roomId}/producers/${producer.id}`,
            { data: this.getMeetingMediaPayload() }
          ).catch(() => undefined);
        }
        try { producer.close(); } catch {}
      }
      this._handleSignalingApiError(error, {
        operation: 'produce-track',
        mediaType: appData?.type || kind,
      });
      console.error(`Error producing ${kind} track:`, error);
      throw error;
    }
  }

  async _retireMigratedLegacyProducer(mediaType, replacementProducer) {
    const legacy = this._legacySendHandover;
    if (!legacy) return;
    const key = String(mediaType || '').toLowerCase();
    const oldProducer =
      key === 'screen'
        ? legacy.screenProducer
        : legacy.producers?.get(key);
    if (!oldProducer || oldProducer === replacementProducer) return;

    try { oldProducer.close(); } catch {}
    if (key === 'screen') legacy.screenProducer = null;
    else legacy.producers.delete(key);

    if (!legacy.producers.size && !legacy.screenProducer) {
      this._legacySendHandover = null;
      await this.closeServerTransport(legacy.transport?.id).catch(
        () => undefined
      );
      try { legacy.transport?.close(); } catch {}
    }
  }

  _scheduleLegacySendRetry(mediaType, attempt = 0) {
    const key = String(mediaType || '').toLowerCase();
    if (!key || this._destroyed || this._legacySendRetryTimers.has(key)) return;
    const delay = Math.min(5000, 600 * (2 ** attempt));
    const timer = window.setTimeout(async () => {
      this._legacySendRetryTimers.delete(key);
      const legacy = this._legacySendHandover;
      const oldProducer =
        key === 'screen' ? legacy?.screenProducer : legacy?.producers?.get(key);
      if (!legacy || !oldProducer || oldProducer.closed || this._destroyed) return;

      const track =
        key === 'screen'
          ? this.screenStream?.getVideoTracks?.().find(
              (item) => item.readyState === 'live'
            )
          : this.localStream?.getTracks?.().find(
              (item) => item.kind === key && item.readyState === 'live'
            );
      if (!track) return;

      try {
        const migrate = async () => {
          const currentTrack =
            key === 'screen'
              ? track
              : this.localStream?.getTracks?.().find(
                  (item) =>
                    item.kind === key && item.readyState === 'live'
                );
          if (!currentTrack) return null;
          const producer = await this.produceTrack({
            track: currentTrack,
            kind: key === 'screen' ? 'video' : key,
            appData:
              key === 'screen'
                ? { type: 'screen' }
                : key === 'audio'
                  ? { processed: true }
                  : {},
          });
          if (key === 'screen') {
            currentTrack.onended = () => this.stopScreenSharing();
          }
          if (
            key !== 'screen' &&
            (
              this._mediaEnabled[key] !== true ||
              currentTrack.enabled === false
            ) &&
            !producer.paused
          ) {
            await producer.pause();
          }
          return producer;
        };
        if (key === 'screen') {
          await migrate();
        } else {
          await this._queueMediaOperation(
            key,
            async (assertCurrent) => {
              const migrated = await migrate();
              assertCurrent();
              return migrated;
            }
          );
        }
      } catch (error) {
        if (key === 'screen') this.screenProducer = oldProducer;
        else this.producers.set(key, oldProducer);
        if (attempt < 4) this._scheduleLegacySendRetry(key, attempt + 1);
      }
    }, delay);
    this._legacySendRetryTimers.set(key, timer);
  }

  async getOrCreateSendTransport() {
    if (!this.sendTransport || this.sendTransport.closed) {
      await this.createSendTransport();
    }
    return this.sendTransport;
  }

  async createSendTransport({ handover = false } = {}) {
    if (this._sendCreatePromise) return this._sendCreatePromise;
    const creation = this._createSendTransport({ handover });
    this._sendCreatePromise = creation;
    try {
      return await creation;
    } finally {
      if (this._sendCreatePromise === creation) {
        this._sendCreatePromise = null;
      }
    }
  }

  async _createSendTransport({ handover = false } = {}) {
    const lifecycleGeneration = this._mediaLifecycleGeneration;
    const assertCurrent = () => {
      if (
        this._destroyed ||
        lifecycleGeneration !== this._mediaLifecycleGeneration
      ) {
        const error = new Error(
          'Meeting changed while creating the send transport.'
        );
        error.code = 'STALE_MEDIA_OPERATION';
        throw error;
      }
    };
    const previousTransport = this.sendTransport;
    let transport = null;
    let allocatedTransportId = '';
    try {
      await this.waitForSignalingReady();
      assertCurrent();
      // Create the replacement before touching the working transport. A failed
      // POST must leave an ongoing meeting exactly as it was.
      const res = await api.post(
        `/mediasoup/${this.roomId}/create-transport`,
        this.getMeetingMediaPayload({ direction: 'send' })
      );
      const { id, iceParameters, iceCandidates, dtlsParameters } = res.data;
      allocatedTransportId = id;
      assertCurrent();

      transport = this.device.createSendTransport({
        id,
        iceParameters,
        iceCandidates,
        dtlsParameters,
      });
      assertCurrent();
      this.sendTransport = transport;

      transport.on('connect', ({ dtlsParameters }, callback, errback) => {
        api.post(
          `/mediasoup/${this.roomId}/connect-transport`,
          this.getMeetingMediaPayload({
            transportId: transport.id,
            dtlsParameters,
          })
        ).then(callback).catch((error) => {
          this._handleSignalingApiError(error, {
            operation: 'connect-send-transport',
          });
          errback(error);
        });
      });

      transport.on('produce', async ({ kind, rtpParameters, appData }, callback, errback) => {
        try {
          const { data } = await api.post(
            `/mediasoup/${this.roomId}/produce`,
            this.getMeetingMediaPayload({
              transportId: transport.id,
              kind,
              rtpParameters,
              appData,
              mediaType: appData?.type || appData?.mediaType || kind,
            })
          );
          callback({ id: data.id });
        } catch (err) {
          this._handleSignalingApiError(err, {
            operation: 'produce',
            mediaType: appData?.type || appData?.mediaType || kind,
          });
          errback(err);
        }
      });

      transport.on('connectionstatechange', (state) => {
        if (this.sendTransport !== transport || this._destroyed) return;
        console.log('Send transport state:', state);
        if (state === 'failed') {
          console.warn('Send transport failed, attempting to recover...');
          this.handleTransportFailure('send');
        } else if (state === 'disconnected') {
          window.setTimeout(() => {
            if (
              this.sendTransport === transport &&
              transport.connectionState === 'disconnected'
            ) {
              this.handleTransportFailure('send');
            }
          }, 6000);
        }
      });

      if (!handover && previousTransport && previousTransport !== transport) {
        await this.closeServerTransport(previousTransport.id).catch(() => undefined);
        assertCurrent();
        try { previousTransport.close(); } catch {}
      }
      assertCurrent();
      return transport;
    } catch (error) {
      this._handleSignalingApiError(error, {
        operation: 'create-send-transport',
      });
      const stale = error?.code === 'STALE_MEDIA_OPERATION';
      if (transport) {
        if (this.sendTransport === transport) {
          this.sendTransport = stale ? null : previousTransport || null;
        }
        await this.closeServerTransport(
          transport.id || allocatedTransportId
        ).catch(() => undefined);
        try { transport.close(); } catch {}
      } else if (allocatedTransportId) {
        await this.closeServerTransport(allocatedTransportId)
          .catch(() => undefined);
      } else if (!stale && !this.sendTransport && previousTransport) {
        this.sendTransport = previousTransport;
      }
      console.error('Error creating send transport:', error);
      throw error;
    }
  }

  async createSendTransportTest() {
    return this.getOrCreateSendTransport();
  }

  async createRecvTransport({ handover = false } = {}) {
    if (this._recvCreatePromise) return this._recvCreatePromise;
    const creation = this._createRecvTransport({ handover });
    this._recvCreatePromise = creation;
    try {
      return await creation;
    } finally {
      if (this._recvCreatePromise === creation) {
        this._recvCreatePromise = null;
      }
    }
  }

  async _createRecvTransport({ handover = false } = {}) {
    const lifecycleGeneration = this._mediaLifecycleGeneration;
    const assertCurrent = () => {
      if (
        this._destroyed ||
        lifecycleGeneration !== this._mediaLifecycleGeneration
      ) {
        const error = new Error(
          'Meeting changed while creating the receive transport.'
        );
        error.code = 'STALE_MEDIA_OPERATION';
        throw error;
      }
    };
    const previousTransport = this.recvTransport;
    const previousGeneration = this._recvGeneration;
    let transport = null;
    let allocatedTransportId = '';
    try {
      await this.waitForSignalingReady();
      assertCurrent();
      // Keep the old receive path alive until the replacement has at least
      // been allocated locally. Failed create requests cannot blank every tile.
      const res = await api.post(
        `/mediasoup/${this.roomId}/create-transport`,
        this.getMeetingMediaPayload({ direction: 'recv' })
      );
      allocatedTransportId = res.data.id;
      assertCurrent();

      transport = this.device.createRecvTransport({
        id: res.data.id,
        iceParameters: res.data.iceParameters,
        iceCandidates: res.data.iceCandidates,
        dtlsParameters: res.data.dtlsParameters,
      });
      assertCurrent();
      this._recvGeneration = previousGeneration + 1;
      const generation = this._recvGeneration;
      this.recvTransport = transport;

      transport.on('connect', ({ dtlsParameters }, callback, errback) => {
        api.post(
          `/mediasoup/${this.roomId}/connect-transport`,
          this.getMeetingMediaPayload({
            transportId: transport.id,
            dtlsParameters,
          })
        ).then(callback).catch((error) => {
          this._handleSignalingApiError(error, {
            operation: 'connect-receive-transport',
          });
          errback(error);
        });
      });

      transport.on('connectionstatechange', (state) => {
        if (
          generation !== this._recvGeneration ||
          this._destroyed
        ) return;
        console.log('Receive transport state:', state);
        if (state === 'failed') {
          console.warn('Receive transport failed, attempting to recover...');
          this.handleTransportFailure('recv');
        } else if (state === 'disconnected') {
          window.setTimeout(() => {
            if (
              this.recvTransport === transport &&
              transport.connectionState === 'disconnected'
            ) {
              this.handleTransportFailure('recv');
            }
          }, 6000);
        }
      });

      if (!handover && previousTransport && previousTransport !== transport) {
        await this.closeServerTransport(previousTransport.id).catch(() => undefined);
        assertCurrent();
        try { previousTransport.close(); } catch {}
        this.activeConsumers.forEach((consumer) => {
          try { consumer.close(); } catch {}
        });
        this.activeConsumers.clear();
        this._bytesCache.clear();
        this._networkStatsCache.clear();
      }
      assertCurrent();
      return transport;
    } catch (error) {
      this._handleSignalingApiError(error, {
        operation: 'create-receive-transport',
      });
      const stale = error?.code === 'STALE_MEDIA_OPERATION';
      if (transport) {
        if (this.recvTransport === transport) {
          this.recvTransport = stale ? null : previousTransport || null;
          if (!stale) this._recvGeneration = previousGeneration;
        }
        await this.closeServerTransport(
          transport.id || allocatedTransportId
        ).catch(() => undefined);
        try { transport.close(); } catch {}
      } else if (allocatedTransportId) {
        await this.closeServerTransport(allocatedTransportId)
          .catch(() => undefined);
      } else if (!stale && !this.recvTransport && previousTransport) {
        this.recvTransport = previousTransport;
        this._recvGeneration = previousGeneration;
      }
      console.error('Error creating receive transport:', error);
      throw error;
    }
  }

  async getOrCreateRecvTransport() {
    if (this.recvTransport && !this.recvTransport.closed) {
      return this.recvTransport;
    }
    return this.createRecvTransport();
  }

  async handleTransportFailure(
    type,
    { forceRebuild = false, reconnectConsumers = true } = {}
  ) {
    if (this._destroyed || !['send', 'recv'].includes(type)) return false;
    const promiseKey = type === 'send'
      ? '_sendRecoveryPromise'
      : '_recvRecoveryPromise';
    if (this[promiseKey]) return this[promiseKey];

    const recovery = (async () => {
      await this.waitForSignalingReady();
      const currentTransport = type === 'send'
        ? this.sendTransport
        : this.recvTransport;

      if (!forceRebuild && currentTransport && !currentTransport.closed) {
        const restarted = await this.restartTransportIce(currentTransport);
        if (restarted) {
          await new Promise((resolve) => window.setTimeout(resolve, 1800));
          if (
            !currentTransport.closed &&
            currentTransport.connectionState === 'connected'
          ) {
            return true;
          }
        }
      }

      console.log(`Rebuilding ${type} transport...`);
      if (type === 'send') {
        const previousTransport = currentTransport;
        const previousProducers = new Map(this.producers);
        const previousScreenProducer = this.screenProducer;
        const producerStates = new Map(
          Array.from(previousProducers.entries()).map(([kind, producer]) => [
            kind,
            { paused: Boolean(producer?.paused), appData: producer?.appData || {} },
          ])
        );
        const activeKinds = [...new Set([
          ...this.expectedMediaKinds,
          ...producerStates.keys(),
        ])].sort((left, right) => {
          if (left === right) return 0;
          if (left === 'audio') return -1;
          if (right === 'audio') return 1;
          return String(left).localeCompare(String(right));
        });
        const activeScreenTrack = this.screenStream?.getVideoTracks()?.find(
          (track) => track.readyState === 'live'
        );

        let replacementTransport = null;
        const migratedKinds = new Set();
        const failedKinds = new Set();
        let screenMigrated = false;
        let screenFailed = false;
        try {
          replacementTransport = await this.createSendTransport({
            handover: true,
          });

          for (const kind of activeKinds) {
            try {
              await this._queueMediaOperation(kind, async (assertCurrent) => {
                let track = this.localStream?.getTracks()?.find(
                  (item) => item.kind === kind
                );
                if (!track || track.readyState !== 'live') {
                  if (this._mediaEnabled[kind] !== true) return;
                  track = kind === 'video'
                    ? await this._reacquireVideoTrack()
                    : await this._reacquireAudioTrack();
                  assertCurrent();
                }
                if (!track || track.readyState !== 'live') return;

                const oldState = producerStates.get(kind);
                const producer = await this.produceTrack({
                  track,
                  kind,
                  appData: oldState?.appData || (
                    kind === 'audio' ? { processed: true } : {}
                  ),
                });
                assertCurrent();
                migratedKinds.add(kind);
                if (
                  oldState?.paused ||
                  this._mediaEnabled[kind] !== true ||
                  track.enabled === false
                ) {
                  track.enabled = false;
                  await producer.pause();
                  assertCurrent();
                }
                assertCurrent();
                this._notifyLocalMediaState(kind, {
                  reason: 'transport-recovered',
                });
              });
            } catch (trackError) {
              const oldProducer = previousProducers.get(kind);
              console.warn(
                `Unable to restore ${kind} after reconnect:`,
                trackError?.message || trackError
              );
              // Once /produce succeeds the backend has atomically replaced the
              // old kind; never roll that successful migration back because a
              // later kind failed. If creation itself failed, keep that kind's
              // old producer and transport alive for a targeted retry.
              if (!migratedKinds.has(kind)) {
                failedKinds.add(kind);
                if (oldProducer && !oldProducer.closed) {
                  this.producers.set(kind, oldProducer);
                } else {
                  await this._handleLocalTrackRecoveryFailure(kind, trackError);
                }
              }
            }
          }

          if (activeScreenTrack) {
            try {
              this.screenProducer = await this.produceTrack({
                track: activeScreenTrack,
                kind: 'video',
                appData: { type: 'screen' },
              });
              screenMigrated = true;
            } catch (screenError) {
              screenFailed = true;
              this.screenProducer = previousScreenProducer || null;
              console.warn(
                'Unable to restore screen share after reconnect:',
                screenError?.message || screenError
              );
            }
          }

          previousProducers.forEach((producer, kind) => {
            if (!migratedKinds.has(kind)) return;
            try { producer?.close(); } catch {}
          });
          if (
            screenMigrated &&
            previousScreenProducer &&
            previousScreenProducer !== this.screenProducer
          ) {
            try { previousScreenProducer.close(); } catch {}
          }

          const legacyProducers = new Map(
            [...previousProducers.entries()].filter(
              ([kind, producer]) =>
                failedKinds.has(kind) && producer && !producer.closed
            )
          );
          const legacyScreenProducer =
            screenFailed &&
            previousScreenProducer &&
            !previousScreenProducer.closed
              ? previousScreenProducer
              : null;

          if (legacyProducers.size || legacyScreenProducer) {
            this._legacySendHandover = {
              transport: previousTransport,
              producers: legacyProducers,
              screenProducer: legacyScreenProducer,
            };
            legacyProducers.forEach((_producer, kind) =>
              this._scheduleLegacySendRetry(kind)
            );
            if (legacyScreenProducer) {
              this._scheduleLegacySendRetry('screen');
            }
          } else if (
            previousTransport &&
            previousTransport !== replacementTransport
          ) {
            await this.closeServerTransport(previousTransport.id).catch(
              () => undefined
            );
            try { previousTransport.close(); } catch {}
          }
        } catch (handoverError) {
          // Only pre-migration failures reach this rollback. Per-kind failures
          // above preserve already-migrated audio/video/screen producers.
          const replacementProducers = new Map(this.producers);
          replacementProducers.forEach((producer, kind) => {
            if (producer !== previousProducers.get(kind)) {
              try { producer?.close(); } catch {}
            }
          });
          if (
            this.screenProducer &&
            this.screenProducer !== previousScreenProducer
          ) {
            try { this.screenProducer.close(); } catch {}
          }
          if (
            replacementTransport &&
            replacementTransport !== previousTransport
          ) {
            await this.closeServerTransport(replacementTransport.id).catch(
              () => undefined
            );
            try { replacementTransport.close(); } catch {}
          }
          this.sendTransport = previousTransport || null;
          this.producers = previousProducers;
          this.screenProducer = previousScreenProducer || null;
          throw handoverError;
        }
      } else {
        const previousTransport = currentTransport;
        const previousGeneration = this._recvGeneration;
        const previousConsumers = new Map(this.activeConsumers);
        const previousSessionMap = new Map(this.producerSessionMap);
        const knownConsumers = Array.from(this.activeConsumers.keys()).map(
          (producerId) => ({
            producerId,
            sessionId: this.producerSessionMap.get(producerId),
          })
        );
        let replacementTransport = null;
        try {
          replacementTransport = await this.createRecvTransport({
            handover: true,
          });
          // Keep old consumer objects alive for visual continuity while the
          // replacement consumers negotiate on the new transport.
          this.activeConsumers = new Map();
          knownConsumers.forEach(({ producerId, sessionId }) => {
            if (sessionId) this.producerSessionMap.set(producerId, sessionId);
          });
          if (reconnectConsumers) {
            await this.reconnectConsumers(
              knownConsumers.map((item) => item.producerId),
              { throwOnTransportError: true }
            );
          }

          previousConsumers.forEach((consumer) => {
            try { consumer?.close(); } catch {}
          });
          if (
            previousTransport &&
            previousTransport !== replacementTransport
          ) {
            await this.closeServerTransport(previousTransport.id).catch(
              () => undefined
            );
            try { previousTransport.close(); } catch {}
          }
        } catch (handoverError) {
          this.activeConsumers.forEach((consumer) => {
            try { consumer?.close(); } catch {}
          });
          if (
            replacementTransport &&
            replacementTransport !== previousTransport
          ) {
            await this.closeServerTransport(replacementTransport.id).catch(
              () => undefined
            );
            try { replacementTransport.close(); } catch {}
          }
          this.recvTransport = previousTransport || null;
          this._recvGeneration = previousGeneration;
          this.activeConsumers = previousConsumers;
          this.producerSessionMap = previousSessionMap;
          previousConsumers.forEach((consumer, producerId) => {
            const remoteSessionId = previousSessionMap.get(producerId);
            if (consumer?.track && remoteSessionId) {
              this.onTrackRecovered?.(
                remoteSessionId,
                consumer.appData?.mediaType ||
                  consumer.appData?.type ||
                  consumer.kind,
                consumer.track,
                producerId
              );
            }
          });
          throw handoverError;
        }
      }

      console.log(`✅ ${type} transport recovered`);
      return true;
    })();

    this[promiseKey] = recovery;
    try {
      return await recovery;
    } catch (error) {
      console.error(`❌ Failed to recover ${type} transport:`, error);
      return false;
    } finally {
      if (this[promiseKey] === recovery) this[promiseKey] = null;
    }
  }

  async reconnectConsumers(
    producerIds = Array.from(this.activeConsumers.keys()),
    { throwOnTransportError = false } = {}
  ) {
    if (!producerIds.length) return;

    console.log(`🔁 Reconnecting ${producerIds.length} consumers...`);

    for (const producerId of producerIds) {
      try {
        await this.closeConsumerByProducer(producerId);

        const newConsumer = await this.consume(producerId);
        if (!newConsumer) continue;
        this.activeConsumers.set(producerId, newConsumer);

        if (this.onTrackRecovered) {
           const remoteSessionId = this.producerSessionMap.get(producerId);
           const mediaType = newConsumer.appData?.mediaType ||
             newConsumer.appData?.type || newConsumer.kind;
           this.onTrackRecovered(
             remoteSessionId,
             mediaType,
             newConsumer.track,
             producerId
           );
        }
      } catch (error) {
        const baseClassification = this._handleSignalingApiError(error, {
          operation: 'consume',
          producerId,
        });
        const classification = classifyMediaOperationError(error, 'consume') ===
          'stale-transport'
          ? 'stale-transport'
          : baseClassification;
        if (classification !== 'stale-producer') {
          console.error(`Failed to reconnect consumer ${producerId}:`, error);
          if (throwOnTransportError) throw error;
        }
        this.activeConsumers.delete(producerId);
      }
    }
  }

  async consume(producerId) {
    const cleanProducerId = String(producerId || '').trim();
    if (!cleanProducerId || this.isProducerStale(cleanProducerId)) return null;
    const existing = this.activeConsumers.get(cleanProducerId);
    if (existing && !existing.closed) return existing;
    if (this.consumePromises.has(cleanProducerId)) {
      return this.consumePromises.get(cleanProducerId);
    }

    // Register the promise before awaiting transport creation. Two simultaneous
    // newProducer/list-sync calls must share the exact same negotiation.
    const request = (async () => {
      await this.getOrCreateRecvTransport();
      return this.consumeWithRetry(cleanProducerId);
    })().finally(() => {
      if (this.consumePromises.get(cleanProducerId) === request) {
        this.consumePromises.delete(cleanProducerId);
      }
    });
    this.consumePromises.set(cleanProducerId, request);
    return request;
  }

  async consumeWithRetry(producerId, retries = 4) {
    let lastError;
    let receiveTransportRebuilt = false;
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const result = await this.tryConsume(producerId);
        return result;
      } catch (error) {
        lastError = error;
        const classification = this._handleSignalingApiError(error, {
          operation: 'consume',
          producerId,
        });
        if (classification === 'stale-producer') {
          this.markProducerStale(producerId);
          return null;
        }
        if (classification === 'meeting-ended') {
          throw error;
        }
        if (classification === 'authentication') {
          const recovered = await this.waitForAuthenticationRecovery();
          if (!recovered || attempt >= retries) throw error;
          this.setSignalingReady(true);
          continue;
        }
        if (
          [
            'access-denied',
            'not-started',
            'rejoin',
          ].includes(classification)
        ) {
          this.setSignalingReady(false);
          throw error;
        }
        // An unclassified 403 may be a brief proxy/deployment race. Keep the
        // already-authorized socket usable so the next producer reconciliation
        // can retry; only machine-readable fatal/rejoin codes disable it.
        if (classification === 'authorization') throw error;
        if (classification === 'stale-transport' && !receiveTransportRebuilt) {
          receiveTransportRebuilt = true;
          const recovered = await this.handleTransportFailure('recv', {
            forceRebuild: true,
            reconnectConsumers: false,
          });
          if (
            !recovered ||
            !this.recvTransport ||
            this.recvTransport.closed
          ) {
            throw error;
          }
        } else if (
          classification === 'stale-transport' &&
          receiveTransportRebuilt &&
          Number(error?.response?.status || 0) === 410 &&
          !String(error?.response?.data?.code || '').trim()
        ) {
          // The replacement receive transport is healthy, so a second bare 410
          // points to an old producer announcement. Ignore it briefly and let
          // the authoritative producer reconciliation supply its replacement.
          this.markProducerStale(producerId, 10000);
          return null;
        }

        if (attempt < retries) {
          const delay =
            classification === 'stale-transport'
              ? 100
              : Math.min(4000, 500 * (2 ** (attempt - 1)));
          await new Promise(resolve => window.setTimeout(resolve, delay));
        }
      }
    }
    throw lastError;
  }

  async tryConsume(producerId) {
    return this.queueConsume(async () => {
      const transport = await this.getOrCreateRecvTransport();
      const generation = this._recvGeneration;
      const res = await api.post(
        `/mediasoup/${this.roomId}/consume`,
        this.getMeetingMediaPayload({
          producerId,
          transportId: transport.id,
          rtpCapabilities: this.device.rtpCapabilities,
          // Keep the server consumer paused until mediasoup-client has created
          // its matching local consumer. This prevents the first RTP burst
          // racing ahead of local SDP setup on busy tile views.
          deferResume: true,
        })
      );

      if (
        this.recvTransport !== transport ||
        transport.closed ||
        generation !== this._recvGeneration
      ) {
        await this.closeServerConsumer(res.data.id).catch(() => undefined);
        const error = new Error('Receive transport changed during consume');
        error.code = 'RECV_TRANSPORT_CHANGED';
        throw error;
      }

      let consumer;
      try {
        consumer = await transport.consume({
          id: res.data.id,
          producerId,
          kind: res.data.kind,
          rtpParameters: res.data.rtpParameters,
          appData: {
            ...(res.data.appData || {}),
            sessionId: res.data.sessionId,
            userId: res.data.userId,
            type: res.data.mediaType || res.data.appData?.type || res.data.kind,
            mediaType: res.data.mediaType || res.data.appData?.mediaType || res.data.kind,
          }
        });
      } catch (error) {
        await this.closeServerConsumer(res.data.id).catch(() => undefined);
        if (classifyMediaApiError(error) === 'stale-transport') {
          error.code = 'RECV_TRANSPORT_REBUILD_REQUIRED';
        }
        throw error;
      }

      if (
        this.recvTransport !== transport ||
        transport.closed ||
        generation !== this._recvGeneration
      ) {
        try { consumer.close(); } catch {}
        await this.closeServerConsumer(res.data.id).catch(() => undefined);
        const error = new Error('Receive transport changed after consume');
        error.code = 'RECV_TRANSPORT_CHANGED';
        throw error;
      }

      try {
        await api.post(
          `/mediasoup/${this.roomId}/consumers/${res.data.id}/resume`,
          this.getMeetingMediaPayload()
        );
        if (consumer.paused) await consumer.resume();
      } catch (error) {
        try { consumer.close(); } catch {}
        await this.closeServerConsumer(res.data.id).catch(() => undefined);
        throw error;
      }

      if (
        this.recvTransport !== transport ||
        transport.closed ||
        generation !== this._recvGeneration
      ) {
        try { consumer.close(); } catch {}
        await this.closeServerConsumer(res.data.id).catch(() => undefined);
        const error = new Error('Receive transport changed while resuming consumer');
        error.code = 'RECV_TRANSPORT_CHANGED';
        throw error;
      }
      this.activeConsumers.set(producerId, consumer);
      this._applyAudioConsumerLatencyHint(
        consumer,
        consumer?.appData?.audioMode === 'worship' ||
          consumer?.appData?.contentType === 'music'
      );
      if (res.data.sessionId) {
        this.producerSessionMap.set(producerId, String(res.data.sessionId));
      }
      const remoteSessionId = String(
        res.data.sessionId || this.producerSessionMap.get(producerId) || ''
      );
      const mediaType = String(
        res.data.mediaType ||
        res.data.appData?.mediaType ||
        res.data.appData?.type ||
        res.data.kind
      ).toLowerCase();
      let cleaned = false;
      const cleanup = (recover = false) => {
        if (cleaned) return;
        cleaned = true;
        if (this.activeConsumers.get(producerId) === consumer) {
          this.activeConsumers.delete(producerId);
          this.producerSessionMap.delete(producerId);
        }
        this._bytesCache.delete(consumer.id || producerId);
        if (recover && !this._destroyed) {
          window.setTimeout(() => {
            this.onConsumerTrackEnded?.({
              producerId,
              sessionId: remoteSessionId,
              mediaType,
            });
          }, 750);
        }
      };
      consumer.on('transportclose', () => cleanup(false));
      consumer.on('producerclose', () => cleanup(true));
      consumer.on?.('producerpause', () => {
        this.onRemoteMediaStateChange?.({
          producerId,
          sessionId: remoteSessionId,
          mediaType,
          enabled: false,
          source: 'producer',
        });
      });
      consumer.on?.('producerresume', () => {
        this.onRemoteMediaStateChange?.({
          producerId,
          sessionId: remoteSessionId,
          mediaType,
          enabled: true,
          source: 'producer',
        });
      });
      consumer.track?.addEventListener?.(
        'ended',
        () => cleanup(true),
        { once: true }
      );
      return consumer;
    });
  }

  async closeConsumerByProducer(producerId) {
    const consumer = this.activeConsumers.get(producerId);
    try { consumer?.close(); } catch {}
    this.activeConsumers.delete(producerId);
    this.producerSessionMap.delete(producerId);
    if (consumer) this._bytesCache.delete(consumer.id || producerId);
    if (consumer) this._networkStatsCache.delete(consumer.id || producerId);
    if (consumer?.id) {
      await this.closeServerConsumer(consumer.id).catch(() => undefined);
    }
  }

  /**
   * Rebuild only the consumers that belong to one remote participant. This is
   * deliberately narrower than rebuilding the receive transport, so a retry on
   * one frozen camera cannot interrupt the rest of the meeting.
   */
  async retryConsumersForSession(sessionId, mediaType = 'video') {
    const cleanSessionId = String(sessionId || '').trim();
    const cleanMediaType = String(mediaType || 'video').toLowerCase();
    if (!cleanSessionId) return 0;

    const candidates = Array.from(this.activeConsumers.entries())
      .filter(([producerId, consumer]) => {
        if (String(this.producerSessionMap.get(producerId) || '') !== cleanSessionId) {
          return false;
        }
        const actualType = String(
          consumer?.appData?.mediaType ||
            consumer?.appData?.type ||
            consumer?.kind ||
            ''
        ).toLowerCase();
        return cleanMediaType === 'all' || actualType === cleanMediaType;
      })
      .map(([producerId]) => producerId);

    let restored = 0;
    for (const producerId of candidates) {
      try {
        await this.closeConsumerByProducer(producerId);
        this.clearProducerStale(producerId);
        // Preserve the source mapping while the server creates the replacement
        // consumer. The consume response will replace it with authoritative data.
        this.producerSessionMap.set(producerId, cleanSessionId);
        const consumer = await this.consume(producerId);
        if (!consumer?.track) continue;
        restored += 1;
        this.onTrackRecovered?.(
          cleanSessionId,
          consumer.appData?.mediaType ||
            consumer.appData?.type ||
            consumer.kind,
          consumer.track,
          producerId
        );
      } catch (error) {
        console.warn(
          `Participant media retry failed for ${cleanSessionId}:`,
          error?.message || error
        );
      }
    }
    return restored;
  }

  /**
   * Sender-side response to a receiver health report. It keeps the existing
   * producer/track whenever possible and never restarts the page or meeting.
   */
  async refreshProducer(kind = 'video') {
    const cleanKind = String(kind || 'video').toLowerCase();
    if (!['audio', 'video'].includes(cleanKind)) return false;
    if (this._mediaEnabled[cleanKind] !== true) return false;
    await this.ensureLocalMedia();
    return this._queueMediaOperation(
      cleanKind,
      (assertCurrent) =>
        this._refreshProducerWithCurrentTrack(cleanKind, assertCurrent)
    );
  }

  async _refreshProducerWithCurrentTrack(
    cleanKind,
    assertCurrent = () => undefined
  ) {
    let track = this.localStream?.getTracks?.().find(
      (item) => item.kind === cleanKind && item.readyState === 'live'
    );
    if (!track || track.readyState !== 'live') return false;

    let producer = this.producers.get(cleanKind);
    if (!producer || producer.closed) {
      producer = await this.produceTrack({
        track,
        kind: cleanKind,
        appData: cleanKind === 'audio'
          ? getMeetingAudioProducerAppData(this._audioMode, {
              processed: true,
            })
          : {},
      });
      assertCurrent();
    } else {
      await producer.replaceTrack({ track });
      assertCurrent();
      if (track.enabled !== false && producer.paused) await producer.resume();
      assertCurrent();
    }
    return Boolean(producer && !producer.closed);
  }

  toggleAudio(enable) {
    const shouldEnable = typeof enable === 'boolean' ? enable : !this._mediaEnabled.audio;
    if(shouldEnable && this.examDictationLocked) return Promise.reject(new Error('Stop answer dictation before unmuting the meeting microphone.'));
    const toggleGeneration = ++this._audioToggleGeneration;
    this._mediaEnabled.audio = shouldEnable;
    // Muting is immediate even if an earlier permission prompt/device capture
    // is still resolving. The queued operation then pauses the sender safely.
    if (!shouldEnable) {
      this.localStream?.getAudioTracks?.().forEach((track) => { track.enabled = false; });
      if (this._micInputTrack) this._micInputTrack.enabled = false;
    }
    return this._queueMediaOperation('audio', async (assertCurrent) => {
      if (toggleGeneration !== this._audioToggleGeneration) return this.getLocalMediaState('audio');
      const assertIntentCurrent = () => {
        assertCurrent();
        if (toggleGeneration !== this._audioToggleGeneration) {
          const error = new Error('A newer microphone action replaced this request.');
          error.code = 'STALE_MEDIA_OPERATION';
          throw error;
        }
      };
      try { return await this._toggleAudioInternal(shouldEnable, assertIntentCurrent); }
      catch (error) {
        if (error?.code === 'STALE_MEDIA_OPERATION' && toggleGeneration !== this._audioToggleGeneration) {
          return this.getLocalMediaState('audio');
        }
        throw error;
      }
    });
  }

  async _toggleAudioInternal(
    enable,
    assertCurrent = () => undefined
  ) {
    const shouldEnable =
      typeof enable === 'boolean' ? enable : !this._mediaEnabled.audio;
    try {
      let producer = this.producers.get('audio');
      let track = this.localStream?.getAudioTracks()?.[0];

      if (shouldEnable) {
        await this._ensureMicrophoneProcessingRunning(assertCurrent);
        assertCurrent();
        track = this.localStream?.getAudioTracks()?.[0];
        const microphoneInputUnavailable =
          !this._micInputTrack ||
          this._micInputTrack.readyState !== 'live' ||
          this._micInputTrack.muted === true;
        if (
          !track ||
          track.readyState !== 'live' ||
          microphoneInputUnavailable
        ) {
          track = await this._reacquireAudioTrack();
          assertCurrent();
        }
        track.enabled = true;
        producer = this.producers.get('audio');
        if (!producer || producer.closed) {
          producer = await this.produceTrack({
            track,
            kind: 'audio',
            appData: getMeetingAudioProducerAppData(this._audioMode, {
              processed: true,
            }),
          });
          assertCurrent();
        } else if (producer.paused) {
          await producer.resume();
          assertCurrent();
        }
        if (this._micInputTrack) this._micInputTrack.enabled = true;
        this._mediaEnabled.audio = true;
        this.updateLocalStream();
      } else {
        this._mediaEnabled.audio = false;
        if (track) track.enabled = false;
        if (this._micInputTrack) this._micInputTrack.enabled = false;
        if (producer && !producer.closed && !producer.paused) {
          await producer.pause();
          assertCurrent();
        }
      }
      assertCurrent();
      return this._notifyLocalMediaState('audio', {
        reason: 'user-toggle',
      });
    } catch (error) {
      console.error('Error in toggleAudio:', error);
      if (shouldEnable && error?.code !== 'STALE_MEDIA_OPERATION') {
        await this._handleLocalTrackRecoveryFailure('audio', error);
      }
      throw error;
    }
  }

  _queueMediaOperation(kind, operation) {
    const lifecycleGeneration = this._mediaLifecycleGeneration;
    const previous =
      this._mediaOperationPromises[kind] || Promise.resolve();
    const assertLifecycleCurrent = () => {
      if (
        this._destroyed ||
        lifecycleGeneration !== this._mediaLifecycleGeneration
      ) {
        const error = new Error(
          'This media request belongs to an earlier meeting session.'
        );
        error.code = 'STALE_MEDIA_OPERATION';
        throw error;
      }
    };
    const run = () => {
      assertLifecycleCurrent();
      return operation(assertLifecycleCurrent);
    };
    const queued = previous.then(run, run);
    this._mediaOperationPromises[kind] = queued.catch(() => undefined);
    return queued;
  }

  reacquireAudioTrack() {
    return this._queueMediaOperation(
      'audio',
      () => this._reacquireAudioTrack()
    );
  }

  async _reacquireAudioTrack() {
    const operationGeneration =
      (this._mediaOperationGeneration.audio || 0) + 1;
    this._mediaOperationGeneration.audio = operationGeneration;
    const assertCurrent = (stream) => {
      if (
        this._destroyed ||
        this._mediaOperationGeneration.audio !== operationGeneration
      ) {
        stream?.getTracks?.().forEach((track) => {
          try { track.stop(); } catch {}
        });
        const error = new Error(
          'Microphone recovery was replaced by a newer device request.'
        );
        error.code = 'STALE_MEDIA_OPERATION';
        throw error;
      }
    };
    const oldLocalStream = this.localStream;
    const oldInputTrack = this._micInputTrack;
    const oldOutgoingTrack = oldLocalStream?.getAudioTracks?.()[0];
    const producer = this.producers.get('audio');
    const producerWasPaused = Boolean(producer?.paused);
    let stream = null;
    let newInputTrack = null;
    let stage = null;
    let producerReplaced = false;
    let committed = false;
    try {
      const selectedDeviceId =
        localStorage.getItem('meeting.audioInputId') || '';
      const attempts = getMeetingAudioConstraintAttempts(
        this._audioMode,
        selectedDeviceId
      );
      let lastError = null;
      for (const audio of attempts) {
        let candidate = null;
        try {
          candidate = await navigator.mediaDevices.getUserMedia({
            audio,
            video: false,
          });
          assertCurrent(candidate);
          if (candidate.getAudioTracks()[0]?.readyState === 'live') {
            stream = candidate;
            break;
          }
          candidate.getTracks().forEach((track) => track.stop());
        } catch (error) {
          candidate?.getTracks?.().forEach((track) => {
            try { track.stop(); } catch {}
          });
          lastError = error;
          if (!shouldRetryAudioCapture(error)) throw error;
        }
      }
      if (!stream?.getAudioTracks?.()[0]) {
        throw lastError || new Error('Microphone unavailable');
      }

      newInputTrack = stream.getAudioTracks()[0];
      await prepareMeetingAudioTrack(newInputTrack, this._audioMode);
      assertCurrent(stream);
      stage = await this._stageAudioInput(newInputTrack);
      assertCurrent(stream);
      const outgoingTrack = stage.outgoingTrack;
      const shouldEnable = this._mediaEnabled.audio === true;
      newInputTrack.enabled = shouldEnable;
      outgoingTrack.enabled = shouldEnable;

      if (
        producer &&
        !producer.closed &&
        producer.track?.id !== outgoingTrack.id
      ) {
        await producer.replaceTrack({ track: outgoingTrack });
        producerReplaced = true;
        assertCurrent(stream);
      }
      if (producer && !producer.closed) {
        if (shouldEnable && producer.paused) await producer.resume();
        if (!shouldEnable && !producer.paused) await producer.pause();
      }
      assertCurrent(stream);

      stage.commit();
      this.localStream = new MediaStream([
        ...(oldLocalStream?.getVideoTracks?.() || []),
        outgoingTrack,
      ]);
      this.expectedMediaKinds.add('audio');
      this._attachLocalTrackHealth(newInputTrack, 'audio');
      store.dispatch(setLocalStream(this.localStream));
      committed = true;

      if (oldInputTrack && oldInputTrack !== newInputTrack) {
        this._detachLocalTrackHealth(oldInputTrack, 'audio');
        try { oldInputTrack.stop(); } catch {}
      }
      if (
        oldOutgoingTrack &&
        oldOutgoingTrack !== oldInputTrack &&
        oldOutgoingTrack !== outgoingTrack
      ) {
        try {
          oldOutgoingTrack.onended = null;
          oldOutgoingTrack.stop();
        } catch {}
      }
      this._notifyMicrophoneInput('capture-recovered');
      return outgoingTrack;
    } catch (error) {
      if (!committed) {
        const mayRestore =
          !this._destroyed &&
          this._mediaOperationGeneration.audio === operationGeneration;
        if (
          producerReplaced &&
          producer &&
          !producer.closed &&
          oldOutgoingTrack?.readyState === 'live' &&
          mayRestore
        ) {
          await producer.replaceTrack({ track: oldOutgoingTrack })
            .catch(() => undefined);
          if (producerWasPaused && !producer.paused) {
            await producer.pause().catch(() => undefined);
          } else if (!producerWasPaused && producer.paused) {
            await producer.resume().catch(() => undefined);
          }
        }
        stage?.rollback?.();
        if (mayRestore) {
          this._micInputTrack = oldInputTrack;
          this.localStream = oldLocalStream;
        }
        stream?.getTracks?.().forEach((track) => {
          if (track !== oldInputTrack && track !== oldOutgoingTrack) {
            try { track.stop(); } catch {}
          }
        });
      }
      console.error('Error reacquiring audio track:', error);
      throw error;
    }
  }

  toggleVideo(enable) {
    const examState = store.getState();
    if (enable === false && examState.room?.currentRoom?.meetingKind === 'exam' && examState.auth?.user?.role === 'student') throw new Error('Your camera must stay on during the exam.');
    return this._queueMediaOperation(
      'video',
      (assertCurrent) =>
        this._toggleVideoInternal(enable, assertCurrent)
    );
  }

  async _toggleVideoInternal(
    enable,
    assertCurrent = () => undefined
  ) {
    const shouldEnable =
      typeof enable === 'boolean' ? enable : !this._mediaEnabled.video;
    try {
      let producer = this.producers.get('video');
      let track = this.localStream?.getVideoTracks()?.[0];

      if (shouldEnable) {
        if (!track || track.readyState !== 'live') {
          track = await this._reacquireVideoTrack();
          assertCurrent();
        }
        track.enabled = true;
        producer = this.producers.get('video');
        if (!producer || producer.closed) {
          producer = await this.produceTrack({
            track,
            kind: 'video',
          });
          assertCurrent();
        } else if (producer.paused) {
          await producer.resume();
          assertCurrent();
        }
        this._mediaEnabled.video = true;
        this.updateLocalStream();
      } else {
        this._mediaEnabled.video = false;
        if (track) track.enabled = false;
        if (producer && !producer.closed && !producer.paused) {
          await producer.pause();
          assertCurrent();
        }
      }
      assertCurrent();
      return this._notifyLocalMediaState('video', {
        reason: 'user-toggle',
      });
    } catch (error) {
      console.error('Error in toggleVideo:', error);
      if (shouldEnable && error?.code !== 'STALE_MEDIA_OPERATION') {
        await this._handleLocalTrackRecoveryFailure('video', error);
      }
      throw error;
    }
  }

  reacquireVideoTrack() {
    return this._queueMediaOperation(
      'video',
      () => this._reacquireVideoTrack()
    );
  }

  async _reacquireVideoTrack() {
    const operationGeneration =
      (this._mediaOperationGeneration.video || 0) + 1;
    this._mediaOperationGeneration.video = operationGeneration;
    const assertCurrent = (stream) => {
      if (
        this._destroyed ||
        this._mediaOperationGeneration.video !== operationGeneration
      ) {
        stream?.getTracks?.().forEach((track) => {
          try { track.stop(); } catch {}
        });
        const error = new Error(
          'Camera recovery was replaced by a newer device request.'
        );
        error.code = 'STALE_MEDIA_OPERATION';
        throw error;
      }
    };
    const oldLocalStream = this.localStream;
    const oldTrack = oldLocalStream?.getVideoTracks?.()[0];
    const producer = this.producers.get('video');
    const producerWasPaused = Boolean(producer?.paused);
    let stream = null;
    let newTrack = null;
    let producerReplaced = false;
    let committed = false;
    try {
      const selectedDeviceId = localStorage.getItem('meeting.videoInputId') || '';
      const attempts = [
        {
          ...(selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : {}),
          width: { ideal: 640, max: 1280 },
          height: { ideal: 360, max: 720 },
          frameRate: { ideal: 15, max: 20 },
          facingMode: { ideal: 'user' },
        },
        {
          width: { ideal: 320, max: 640 },
          height: { ideal: 180, max: 360 },
          frameRate: { ideal: 8, max: 12 },
          facingMode: { ideal: 'user' },
        },
        true,
      ];
      let lastError = null;
      for (const video of attempts) {
        let candidate = null;
        try {
          candidate = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video,
          });
          assertCurrent(candidate);
          if (candidate.getVideoTracks()[0]) {
            stream = candidate;
            break;
          }
          candidate.getTracks().forEach((track) => track.stop());
        } catch (error) {
          candidate?.getTracks?.().forEach((track) => {
            try { track.stop(); } catch {}
          });
          lastError = error;
        }
      }
      if (!stream?.getVideoTracks?.()[0]) throw lastError || new Error('Camera unavailable');

      newTrack = stream.getVideoTracks()[0];
      assertCurrent(stream);
      if (!newTrack) throw new Error('No video track found in new stream');
      try { newTrack.contentHint = 'motion'; } catch {}
      this.expectedMediaKinds.add('video');
      newTrack.enabled = this._mediaEnabled.video === true;

      if (producer && !producer.closed) {
        await this.replaceTrack('video', newTrack);
        producerReplaced = true;
        assertCurrent(stream);
      }

      assertCurrent(stream);
      this.localStream = new MediaStream([
        ...(oldLocalStream?.getAudioTracks?.() || []),
        newTrack,
      ]);
      this._attachLocalTrackHealth(newTrack, 'video');
      store.dispatch(setLocalStream(this.localStream));
      await this._reapplyCurrentCameraQuality();
      committed = true;
      if (oldTrack && oldTrack !== newTrack) {
        this._detachLocalTrackHealth(oldTrack, 'video');
        oldTrack.stop();
      }
      return newTrack; 
    } catch (error) {
      if (!committed) {
        const mayRestore =
          !this._destroyed &&
          this._mediaOperationGeneration.video === operationGeneration;
        if (
          producerReplaced &&
          producer &&
          !producer.closed &&
          oldTrack?.readyState === 'live' &&
          mayRestore
        ) {
          await producer.replaceTrack({ track: oldTrack })
            .catch(() => undefined);
          if (producerWasPaused && !producer.paused) {
            await producer.pause().catch(() => undefined);
          } else if (!producerWasPaused && producer.paused) {
            await producer.resume().catch(() => undefined);
          }
        }
        if (mayRestore) this.localStream = oldLocalStream;
        stream?.getTracks?.().forEach((track) => {
          if (track !== oldTrack) {
            try { track.stop(); } catch {}
          }
        });
      }
      console.error('Error reacquiring video track:', error);
      throw error;
    }
  }

  updateLocalStream() {
    if (!this.localStream) return;
    const videoTracks = this.localStream.getVideoTracks() || [];
    const audioTracks = this.localStream.getAudioTracks() || [];
    const newStream = new MediaStream([...videoTracks, ...audioTracks]);
    this.localStream = newStream;
    store.dispatch(setLocalStream(newStream));
  }

  switchInputDevice(kind, deviceId) {
    if (!['audio', 'video'].includes(kind)) {
      return Promise.reject(
        new Error('Device kind must be audio or video')
      );
    }
    this._manualDeviceSwitchPending[kind] += 1;
    return this._queueMediaOperation(
      kind,
      () => this._switchInputDevice(kind, deviceId)
    ).finally(() => {
      this._manualDeviceSwitchPending[kind] = Math.max(
        0,
        this._manualDeviceSwitchPending[kind] - 1
      );
    });
  }

  async _switchInputDevice(
    kind,
    deviceId,
    { persist = true } = {}
  ) {
    if (!['audio', 'video'].includes(kind)) {
      throw new Error('Device kind must be audio or video');
    }
    const operationGeneration =
      (this._mediaOperationGeneration[kind] || 0) + 1;
    this._mediaOperationGeneration[kind] = operationGeneration;
    const assertCurrent = (stream) => {
      if (
        this._destroyed ||
        this._mediaOperationGeneration[kind] !== operationGeneration
      ) {
        stream?.getTracks?.().forEach((track) => {
          try { track.stop(); } catch {}
        });
        const error = new Error(
          'A newer device selection replaced this request.'
        );
        error.code = 'STALE_MEDIA_OPERATION';
        throw error;
      }
    };
    const cleanDeviceId = String(deviceId || '').trim();
    const oldLocalStream = this.localStream;
    const oldInputTrack = kind === 'audio'
      ? this._micInputTrack
      : oldLocalStream?.getVideoTracks?.()[0];
    const oldOutgoingTrack = kind === 'audio'
      ? oldLocalStream?.getAudioTracks?.()[0]
      : oldInputTrack;
    const producer = this.producers.get(kind);
    const producerWasPaused = Boolean(producer?.paused);
    let replacementStream = null;
    let newInputTrack = null;
    let outgoingTrack = null;
    let stage = null;
    let producerReplaced = false;
    let createdProducer = null;
    let committed = false;
    let lastError = null;
    try {
      if (kind === 'audio') {
        for (const audio of getMeetingAudioConstraintAttempts(
          this._audioMode,
          cleanDeviceId,
          true
        )) {
          let candidate = null;
          try {
            candidate = await navigator.mediaDevices.getUserMedia({
              audio,
              video: false,
            });
            assertCurrent(candidate);
            if (candidate.getAudioTracks()[0]?.readyState === 'live') {
              replacementStream = candidate;
              break;
            }
            candidate.getTracks().forEach((track) => track.stop());
          } catch (error) {
            candidate?.getTracks?.().forEach((track) => {
              try { track.stop(); } catch {}
            });
            lastError = error;
            if (!shouldRetryAudioCapture(error)) throw error;
          }
        }
      } else {
        replacementStream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            ...(cleanDeviceId ? { deviceId: { exact: cleanDeviceId } } : {}),
            width: { ideal: 640, max: 1280 },
            height: { ideal: 360, max: 720 },
            frameRate: { ideal: 15, max: 24 },
          },
        });
        assertCurrent(replacementStream);
      }

      newInputTrack = kind === 'audio'
        ? replacementStream?.getAudioTracks?.()[0]
        : replacementStream?.getVideoTracks?.()[0];
      if (!newInputTrack) {
        throw lastError ||
          new Error(`Selected ${kind} device did not provide a track`);
      }
      if (kind === 'audio') {
        await prepareMeetingAudioTrack(newInputTrack, this._audioMode);
        assertCurrent(replacementStream);
        stage = await this._stageAudioInput(newInputTrack);
        outgoingTrack = stage.outgoingTrack;
      } else {
        try { newInputTrack.contentHint = 'motion'; } catch {}
        outgoingTrack = newInputTrack;
      }

      const shouldEnable = this._mediaEnabled[kind] === true;
      newInputTrack.enabled = shouldEnable;
      outgoingTrack.enabled = shouldEnable;
      if (
        producer &&
        !producer.closed &&
        producer.track?.id !== outgoingTrack.id
      ) {
        await producer.replaceTrack({ track: outgoingTrack });
        producerReplaced = true;
      }
      if (producer && !producer.closed) {
        if (shouldEnable && producer.paused) await producer.resume();
        if (!shouldEnable && !producer.paused) await producer.pause();
      }
      assertCurrent(replacementStream);

      stage?.commit?.();
      const otherTracks = kind === 'audio'
        ? oldLocalStream?.getVideoTracks?.() || []
        : oldLocalStream?.getAudioTracks?.() || [];
      this.localStream = new MediaStream([...otherTracks, outgoingTrack]);
      store.dispatch(setLocalStream(this.localStream));

      // When the user is muted/camera-off, keep the selected local track ready
      // but do not publish a producer until an explicit enable action.
      if ((!producer || producer.closed) && shouldEnable) {
        createdProducer = await this.produceTrack({
          track: outgoingTrack,
          kind,
          appData: kind === 'audio'
            ? getMeetingAudioProducerAppData(this._audioMode, {
                processed: outgoingTrack !== newInputTrack,
              })
            : {},
        });
      }
      assertCurrent(replacementStream);

      const finalShouldEnable = this._mediaEnabled[kind] === true;
      newInputTrack.enabled = finalShouldEnable;
      outgoingTrack.enabled = finalShouldEnable;
      const finalProducer = this.producers.get(kind);
      if (finalProducer && !finalProducer.closed) {
        if (finalShouldEnable && finalProducer.paused) {
          await finalProducer.resume();
        } else if (!finalShouldEnable && !finalProducer.paused) {
          await finalProducer.pause();
        }
      }
      assertCurrent(replacementStream);
      if (kind === 'video') await this._reapplyCurrentCameraQuality();

      this.expectedMediaKinds.add(kind);
      this._attachLocalTrackHealth(newInputTrack, kind);
      if (persist) {
        const storageKey =
          kind === 'audio'
            ? 'meeting.audioInputId'
            : 'meeting.videoInputId';
        if (cleanDeviceId) localStorage.setItem(storageKey, cleanDeviceId);
        else localStorage.removeItem(storageKey);
      }
      committed = true;

      if (oldInputTrack && oldInputTrack !== newInputTrack) {
        this._detachLocalTrackHealth(oldInputTrack, kind);
        try { oldInputTrack.stop(); } catch {}
      }
      if (
        oldOutgoingTrack &&
        oldOutgoingTrack !== oldInputTrack &&
        oldOutgoingTrack !== outgoingTrack
      ) {
        try {
          oldOutgoingTrack.onended = null;
          oldOutgoingTrack.stop();
        } catch {}
      }
      if (kind === 'audio') this._notifyMicrophoneInput('device-switched');
      this._notifyLocalMediaState(kind, {
        reason: 'device-switched',
      });
      return this.localStream;
    } catch (error) {
      if (!committed) {
        const mayRestore =
          !this._destroyed &&
          this._mediaOperationGeneration[kind] === operationGeneration;
        if (createdProducer) {
          if (this.producers.get(kind) === createdProducer) {
            this.producers.delete(kind);
          }
          if (createdProducer.id) {
            await api.delete(
              `/mediasoup/${this.roomId}/producers/${createdProducer.id}`,
              { data: this.getMeetingMediaPayload() }
            ).catch(() => undefined);
          }
          try { createdProducer.close(); } catch {}
        } else if (
          producerReplaced &&
          producer &&
          !producer.closed &&
          oldOutgoingTrack?.readyState === 'live' &&
          mayRestore
        ) {
          await producer.replaceTrack({ track: oldOutgoingTrack })
            .catch(() => undefined);
          if (producerWasPaused && !producer.paused) {
            await producer.pause().catch(() => undefined);
          } else if (!producerWasPaused && producer.paused) {
            await producer.resume().catch(() => undefined);
          }
        }
        stage?.rollback?.();
        if (mayRestore) {
          if (kind === 'audio') this._micInputTrack = oldInputTrack;
          this.localStream = oldLocalStream;
          store.dispatch(setLocalStream(oldLocalStream || null));
          if (kind === 'audio') this._notifyMicrophoneInput('device-switch-restored');
        }
        replacementStream?.getTracks?.().forEach((track) => {
          if (track !== oldInputTrack && track !== oldOutgoingTrack) {
            try { track.stop(); } catch {}
          }
        });
        if (kind === 'video' && mayRestore) {
          await this._reapplyCurrentCameraQuality().catch(() => undefined);
        }
      }
      throw error;
    }
  }

  _notifyLocalScreenStream(stream) {
    const activeStream =
      stream?.getVideoTracks?.().some(
        (track) => track.readyState === 'live'
      )
        ? stream
        : null;
    store.dispatch(toggleScreenSharing(Boolean(activeStream)));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('meeting-local-screen-stream-change', {
          detail: { stream: activeStream },
        })
      );
    }
  }

  async _produceScreenTrackWithRecovery(screenTrack) {
    const publish = () => this.produceTrack({
      track: screenTrack,
      kind: 'video',
      appData: { type: 'screen' },
    });

    try {
      return await publish();
    } catch (error) {
      // The server can retire an unconnected/orphaned transport without the
      // browser-side object becoming `closed`. Rebuild once and reuse the
      // already selected display track so Chrome never shows a second chooser.
      if (
        classifyMediaApiError(error) !== 'stale-transport' ||
        this._destroyed ||
        screenTrack?.readyState !== 'live'
      ) {
        throw error;
      }

      console.warn(
        'Screen publish found a stale send transport; rebuilding once.'
      );
      const recovered = await this.handleTransportFailure('send', {
        forceRebuild: true,
      });
      if (
        !recovered ||
        this._destroyed ||
        screenTrack?.readyState !== 'live'
      ) {
        throw error;
      }
      return publish();
    }
  }

  _getScreenShareFailureMessage(error, stage) {
    if (['NotAllowedError', 'AbortError'].includes(error?.name)) {
      return 'Screen sharing permission was cancelled or denied';
    }
    if (stage === 'capture' && error?.name === 'NotReadableError') {
      return 'Screen capture could not start. Close any other screen recorder or active share, then try again.';
    }
    if (stage === 'capture' && error?.name === 'InvalidStateError') {
      return 'Open the meeting tab and click Share again to start screen sharing.';
    }
    if (stage === 'capture') {
      return 'Screen capture could not start. Please try again.';
    }
    if (classifyMediaApiError(error) === 'stale-transport') {
      return 'Screen was selected, but the meeting connection could not recover. Please try again.';
    }
    return 'Screen was selected, but it could not connect to the meeting. Please try again.';
  }

  async startScreenSharing() {
    if (this._screenShareStartPromise) return this._screenShareStartPromise;
    if (
      this.screenProducer &&
      !this.screenProducer.closed &&
      this.screenStream?.getVideoTracks?.().some(
        (track) => track.readyState === 'live'
      )
    ) {
      this._notifyLocalScreenStream(this.screenStream);
      return true;
    }

    const operation = (async () => {
      let stream = null;
      let producer = null;
      let stage = 'capture';
      try {
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: { frameRate: { ideal: 15, max: 24 } },
          audio: false,
        });
        if (this._destroyed) {
          throw new Error('Meeting closed while screen sharing was starting.');
        }
        const screenTrack = stream.getVideoTracks()[0];
        if (!screenTrack) throw new Error('No screen track available');

        stage = 'publish';
        producer = await this._produceScreenTrackWithRecovery(screenTrack);
        if (this._destroyed || screenTrack.readyState !== 'live') {
          throw new Error('Screen capture ended before it connected.');
        }

        this.screenStream = stream;
        this.screenProducer = producer;
        screenTrack.onended = () => {
          void this.stopScreenSharing();
        };
        this._notifyLocalScreenStream(stream);

        this.socket?.emit('screen-sharing', {
          roomId: this.roomId,
          userId: this.userId,
          sessionId: this.getMeetingSessionId(),
          isSharing: true,
          producerId: producer.id
        });

        return true;
      } catch (err) {
        try { producer?.close(); } catch {}
        stream?.getTracks?.().forEach((track) => {
          track.onended = null;
          track.stop();
        });
        if (this.screenStream === stream) this.screenStream = null;
        if (this.screenProducer === producer) this.screenProducer = null;
        this._notifyLocalScreenStream(null);
        console.error(`Screen sharing failed during ${stage}:`, err);
        if (!this._destroyed) {
          toast.error(this._getScreenShareFailureMessage(err, stage));
        }
        return false;
      }
    })().finally(() => {
      if (this._screenShareStartPromise === operation) {
        this._screenShareStartPromise = null;
      }
    });
    this._screenShareStartPromise = operation;
    return operation;
  }

  async startNativeScreenSharing(isMobile) {
    const constraintsList = [
      { video: { displaySurface: 'browser', frameRate: { ideal: 15, max: 30 } }, audio: false },
      { video: { displaySurface: 'window', frameRate: { ideal: 15, max: 30 } }, audio: false },
      { video: { displaySurface: 'monitor', frameRate: { ideal: 15, max: 30 } }, audio: false },
      { video: true, audio: false }
    ];

    let stream, lastErr;
    for (const c of constraintsList) {
      try {
        stream = await navigator.mediaDevices.getDisplayMedia(c);
        if (stream) break;
      } catch (e) {
        lastErr = e;
      }
    }
    if (!stream) throw lastErr || new Error('getDisplayMedia failed');

    const screenTrack = stream.getVideoTracks()[0];
    if (!screenTrack) throw new Error('No screen track');

    this.screenStream = stream;

    try {
      this.screenProducer = await this.produceTrack({
        track: screenTrack,
        kind: 'video',
        appData: { type: 'screen', isMobile },
      });
    } catch (error) {
      stream.getTracks().forEach((track) => track.stop());
      if (this.screenStream === stream) this.screenStream = null;
      this.screenProducer = null;
      throw error;
    }

    screenTrack.onended = () => this.stopScreenSharing();

    this.socket?.emit('screen-sharing', {
      roomId: this.roomId,
      userId: this.userId,
      sessionId: this.getMeetingSessionId(),
      isSharing: true,
      producerId: this.screenProducer.id,
      isMobile: isMobile
    });

    this._notifyLocalScreenStream(stream);
    return true;
  }

  async stopScreenSharing() {
    if (this._screenShareStopPromise) return this._screenShareStopPromise;
    const operation = (async () => {
      // If the user presses Stop while the chooser/producer is still
      // resolving, wait for that single start attempt and then tear it down.
      if (this._screenShareStartPromise) {
        await this._screenShareStartPromise.catch(() => false);
      }
      const producer = this.screenProducer;
      const stream = this.screenStream;
      this.screenProducer = null;
      this.screenStream = null;
      let deleteError = null;
      try {
        if (producer?.id) {
          await api.delete(
            `/mediasoup/${this.roomId}/producers/${producer.id}`,
            { data: this.getMeetingMediaPayload() }
          ).catch((error) => {
            if (![404, 410].includes(error?.response?.status)) throw error;
          });
        }
      } catch (error) {
        deleteError = error;
        console.warn(
          'Server could not confirm screen producer removal:',
          error?.message || error
        );
      } finally {
        try { producer?.close(); } catch {}
        stream?.getTracks?.().forEach((track) => {
          track.onended = null;
          try { track.stop(); } catch {}
        });
        this._notifyLocalScreenStream(null);
        this.socket?.emit('screen-sharing', {
          roomId: this.roomId,
          userId: this.userId,
          sessionId: this.getMeetingSessionId(),
          isSharing: false,
          isMobile:
            isMobileDevice() ||
            /iPhone|iPad|iPod/.test(navigator.userAgent)
        });
      }
      // Local OS capture is safely stopped even if the idempotent server
      // cleanup request failed. A reconnect cleanup will retire the stale ID.
      return !deleteError;
    })().finally(() => {
      if (this._screenShareStopPromise === operation) {
        this._screenShareStopPromise = null;
      }
    });
    this._screenShareStopPromise = operation;
    return operation;
  }

  async startIOSScreenSimulation() {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'environment',
        width: { ideal: 1280 },
        height: { ideal: 720 },
        backgroundBlur: true
      }
    });

    const track = stream.getVideoTracks()[0];
    track.contentHint = 'detail';

    this.screenStream = stream;

    this.screenProducer = await this.produceTrack({
      track,
      kind: 'video',
      appData: { type: 'screen', isIOS: true, isSimulated: true },
    });

    this.socket?.emit('screen-sharing', {
      roomId: this.roomId,
      userId: this.userId,
      sessionId: this.getMeetingSessionId(),
      isSharing: true,
      producerId: this.screenProducer.id,
      isMobile: true
    });

    this._notifyLocalScreenStream(stream);
    return true;
  }

  showIOSPWAPrompt() {
    const message = `
      <div style="text-align: left;">
        <p><strong>Enable Screen Sharing on iOS</strong></p>
        <ol style="margin-left: 1em; font-size: 14px;">
          <li>Open in Safari</li>
          <li>Tap the <strong>Share</strong> button</li>
          <li>Choose <strong>Add to Home Screen</strong></li>
          <li>Then reopen the app from your home screen</li>
        </ol>
        <p style="margin-top: 0.5em; font-size: 12px; color: gray;">
          iOS does not allow screen sharing in browser tabs.
        </p>
      </div>
    `;
    toast.info(message, {
      autoClose: 10000,
      closeOnClick: true,
      pauseOnHover: true
    });
  }

  async startRecording() {
    return meetingRecorder.start({ roomId: this.roomId, title: 'Meeting Recording' });
  }

  async stopRecording() {
    return meetingRecorder.stop();
  }

  async getLocalSendStats() {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const wallNow = Date.now();
    let bitrateKbps = 0;
    let audioBitrateKbps = 0;
    let videoBitrateKbps = 0;
    let screenBitrateKbps = 0;
    let hasBitrateSample = false;
    let packetsSent = 0;
    let lossPacketDelta = 0;
    let sentPacketDelta = 0;
    let hasLossSample = false;
    let rttMs = null;
    let jitterMs = null;
    let qualityLimitationReason = '';
    let availableOutgoingKbps = null;

    const producerEntries = Array.from(this.producers.entries());
    if (
      this.screenProducer &&
      !this.screenProducer.closed &&
      !producerEntries.some(([, producer]) => producer === this.screenProducer)
    ) {
      producerEntries.push(['screen', this.screenProducer]);
    }

    await Promise.all(
      producerEntries.map(async ([kind, producer]) => {
        if (!producer || producer.closed) return;
        try {
          const stats = await producer.getStats();
          let bytes = 0;
          let sent = 0;
          let remoteLost = 0;
          let hasRemoteInbound = false;
          let remoteReportMarker = '';

          stats.forEach((report) => {
            if (
              report.type === 'outbound-rtp' &&
              !report.isRemote &&
              report.kind !== 'video-rtx'
            ) {
              bytes += Number(report.bytesSent || 0);
              sent += Number(report.packetsSent || 0);
              if (report.qualityLimitationReason) {
                qualityLimitationReason = String(
                  report.qualityLimitationReason
                ).toLowerCase();
              }
            }
            if (report.type === 'remote-inbound-rtp') {
              hasRemoteInbound = true;
              remoteLost += Math.max(0, Number(report.packetsLost || 0));
              remoteReportMarker += `${Number(
                report.roundTripTimeMeasurements || 0
              )}:${Number(report.totalRoundTripTime || 0)}:${Number(
                report.packetsLost || 0
              )}|`;
              if (Number.isFinite(report.roundTripTime)) {
                rttMs = Math.max(rttMs || 0, report.roundTripTime * 1000);
              }
              if (Number.isFinite(report.jitter)) {
                jitterMs = Math.max(jitterMs || 0, report.jitter * 1000);
              }
            }
          });

          const cacheKey = producer.id || kind;
          const previous = this._localStatsCache.get(cacheKey);
          let producerBitrateKbps = 0;
          if (previous && bytes >= previous.bytes) {
            const elapsedMs = Math.max(1, now - previous.timestamp);
            producerBitrateKbps = ((bytes - previous.bytes) * 8) / elapsedMs;
            bitrateKbps += producerBitrateKbps;
            hasBitrateSample = true;
            if (kind === 'audio') audioBitrateKbps += producerBitrateKbps;
            if (kind === 'video' || kind === 'screen') {
              videoBitrateKbps += producerBitrateKbps;
            }
            if (kind === 'screen') screenBitrateKbps += producerBitrateKbps;

            if (
              hasRemoteInbound &&
              previous.hasRemoteInbound &&
              remoteReportMarker &&
              remoteReportMarker !== previous.remoteReportMarker &&
              sent >= previous.sentAtLastRemoteReport &&
              remoteLost >= previous.lostAtLastRemoteReport
            ) {
              const currentSentDelta =
                sent - previous.sentAtLastRemoteReport;
              const currentLostDelta =
                remoteLost - previous.lostAtLastRemoteReport;
              // remote-inbound-rtp does not define packetsReceived. Match its
              // cumulative packetsLost delta against outbound packetsSent.
              if (currentSentDelta > 0) {
                sentPacketDelta += currentSentDelta;
                lossPacketDelta += Math.min(
                  currentSentDelta,
                  currentLostDelta
                );
                hasLossSample = true;
              }
            }
          }
          const remoteReportAdvanced =
            hasRemoteInbound &&
            remoteReportMarker &&
            remoteReportMarker !== previous?.remoteReportMarker;
          this._localStatsCache.set(cacheKey, {
            bytes,
            sent,
            remoteLost,
            hasRemoteInbound,
            remoteReportMarker,
            sentAtLastRemoteReport: remoteReportAdvanced
              ? sent
              : previous?.sentAtLastRemoteReport ?? sent,
            lostAtLastRemoteReport: remoteReportAdvanced
              ? remoteLost
              : previous?.lostAtLastRemoteReport ?? remoteLost,
            timestamp: now,
            lastProgressAt:
              previous && bytes === previous.bytes
                ? previous.lastProgressAt
                : wallNow,
          });

          packetsSent += sent;
        } catch {
          // A single producer stats failure must not hide other live tracks.
        }
      })
    );

    try {
      const transportStats = await this.sendTransport?.getStats?.();
      transportStats?.forEach?.((report) => {
        if (
          report.type === 'candidate-pair' &&
          (report.nominated || report.selected) &&
          Number.isFinite(report.availableOutgoingBitrate)
        ) {
          availableOutgoingKbps = Math.max(
            availableOutgoingKbps || 0,
            report.availableOutgoingBitrate / 1000
          );
        }
        if (
          report.type === 'candidate-pair' &&
          (report.nominated || report.selected) &&
          Number.isFinite(report.currentRoundTripTime)
        ) {
          rttMs = Math.max(
            rttMs || 0,
            report.currentRoundTripTime * 1000
          );
        }
      });
    } catch {
      // Candidate-pair capacity is optional in several browser engines.
    }

    const packetLoss =
      hasLossSample && sentPacketDelta > 0
        ? Math.min(100, (lossPacketDelta / sentPacketDelta) * 100)
        : null;
    const connectionState = this.sendTransport?.connectionState || 'new';
    const mediaStatus = (kind, producer, track, enabled) => {
      if (!producer || producer.closed || !track) return 'off';
      if (track.readyState !== 'live') return 'unavailable';
      if (!enabled || producer.paused || track.enabled === false) {
        return 'paused';
      }
      if (track.muted) return 'stalled';
      return 'sending';
    };
    const audioProducer = this.producers.get('audio');
    const videoProducer = this.producers.get('video');
    const audioTrack =
      this._micInputTrack || this.localStream?.getAudioTracks?.()[0];
    const videoTrack = this.localStream?.getVideoTracks?.()[0];
    const screenTrack = this.screenStream?.getVideoTracks?.()[0];
    const media = {
      audio: {
        status: mediaStatus(
          'audio',
          audioProducer,
          audioTrack,
          this._mediaEnabled.audio
        ),
      },
      video: {
        status: mediaStatus(
          'video',
          videoProducer,
          videoTrack,
          this._mediaEnabled.video
        ),
        bandwidthLimited: qualityLimitationReason === 'bandwidth',
      },
      screen: {
        status: mediaStatus(
          'screen',
          this.screenProducer,
          screenTrack,
          Boolean(this.screenProducer)
        ),
      },
    };

    return {
      bitrateKbps: hasBitrateSample
        ? Math.round(Math.max(0, bitrateKbps))
        : null,
      audioBitrateKbps: hasBitrateSample
        ? Math.round(Math.max(0, audioBitrateKbps))
        : null,
      videoBitrateKbps: hasBitrateSample
        ? Math.round(Math.max(0, videoBitrateKbps))
        : null,
      screenBitrateKbps: hasBitrateSample
        ? Math.round(Math.max(0, screenBitrateKbps))
        : null,
      packetsSent,
      packetLoss:
        packetLoss == null ? null : Number(packetLoss.toFixed(1)),
      rttMs: Number.isFinite(rttMs) ? Math.round(rttMs) : null,
      jitterMs: Number.isFinite(jitterMs) ? Math.round(jitterMs) : null,
      availableOutgoingKbps:
        availableOutgoingKbps == null
          ? null
          : Math.round(availableOutgoingKbps),
      qualityLimitationReason,
      media,
      isOnline:
        (typeof navigator === 'undefined' || navigator.onLine) &&
        !['failed', 'closed'].includes(connectionState),
      connectionState,
      sampledAt: wallNow,
    };
  }

  /**
   * Returns receiver-side WebRTC measurements grouped by remote session. Stats
   * are calculated from byte/packet deltas rather than browser connection hints.
   */
  async getNetworkStatsSnapshot() {
    const result = {};
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const wallNow = Date.now();
    const transportState = this.recvTransport?.connectionState || 'new';

    await Promise.all(
      Array.from(this.activeConsumers.entries()).map(
        async ([producerId, consumer]) => {
          const sessionId = String(
            this.producerSessionMap.get(producerId) ||
              consumer?.appData?.sessionId ||
              ''
          );
          if (!sessionId || !consumer || consumer.closed) return;

          const mediaType = String(
            consumer.appData?.mediaType ||
              consumer.appData?.type ||
              consumer.kind ||
              ''
          ).toLowerCase();
          const key = consumer.id || producerId;

          try {
            const stats = await consumer.getStats();
            let bytes = 0;
            let packetsReceived = 0;
            let packetsLost = 0;
            let framesDecoded = 0;
            let frameWidth = 0;
            let frameHeight = 0;
            let framesPerSecond = null;
            let jitterMs = null;
            let rttMs = null;

            stats.forEach((report) => {
              if (report.type === 'inbound-rtp' && !report.isRemote) {
                bytes += Number(report.bytesReceived || 0);
                packetsReceived += Number(report.packetsReceived || 0);
                packetsLost += Math.max(0, Number(report.packetsLost || 0));
                framesDecoded += Number(report.framesDecoded || 0);
                frameWidth = Math.max(
                  frameWidth,
                  Number(report.frameWidth || 0)
                );
                frameHeight = Math.max(
                  frameHeight,
                  Number(report.frameHeight || 0)
                );
                if (Number.isFinite(report.framesPerSecond)) {
                  framesPerSecond = Math.max(
                    framesPerSecond || 0,
                    Number(report.framesPerSecond)
                  );
                }
                if (Number.isFinite(report.jitter)) {
                  jitterMs = Math.max(jitterMs || 0, report.jitter * 1000);
                }
              }
              if (
                report.type === 'remote-outbound-rtp' &&
                Number.isFinite(report.roundTripTime)
              ) {
                rttMs = Math.max(rttMs || 0, report.roundTripTime * 1000);
              }
              if (
                report.type === 'candidate-pair' &&
                (report.nominated || report.selected) &&
                Number.isFinite(report.currentRoundTripTime)
              ) {
                rttMs = Math.max(rttMs || 0, report.currentRoundTripTime * 1000);
              }
            });

            const previous = this._networkStatsCache.get(key);
            let bitrateKbps = 0;
            let packetLoss = 0;
            let receivedDelta = 0;
            let lostDelta = 0;
            let hasPacketSample = false;
            let hasBitrateSample = false;
            let progressed = !previous;
            if (previous) {
              const elapsedMs = Math.max(1, now - previous.timestamp);
              const byteDelta = Math.max(0, bytes - previous.bytes);
              receivedDelta = Math.max(
                0,
                packetsReceived - previous.packetsReceived
              );
              lostDelta = Math.max(0, packetsLost - previous.packetsLost);
              const packetDeltaTotal = receivedDelta + lostDelta;
              bitrateKbps = (byteDelta * 8) / elapsedMs;
              hasBitrateSample = true;
              hasPacketSample = packetDeltaTotal > 0;
              packetLoss = packetDeltaTotal > 0
                ? Math.min(100, (lostDelta / packetDeltaTotal) * 100)
                : 0;
              if (
                framesPerSecond == null &&
                framesDecoded >= previous.framesDecoded
              ) {
                framesPerSecond =
                  ((framesDecoded - previous.framesDecoded) * 1000) /
                  elapsedMs;
              }
              progressed =
                byteDelta > 0 ||
                receivedDelta > 0 ||
                framesDecoded > previous.framesDecoded;
            }

            const lastProgressAt = progressed
              ? wallNow
              : previous?.lastProgressAt || wallNow;
            this._networkStatsCache.set(key, {
              bytes,
              packetsReceived,
              packetsLost,
              framesDecoded,
              timestamp: now,
              lastProgressAt,
            });

            let mediaStatus = 'receiving';
            if (
              consumer.track?.readyState === 'ended' ||
              ['failed', 'closed'].includes(transportState)
            ) {
              mediaStatus = 'unavailable';
            } else if (consumer.paused || consumer.track?.enabled === false) {
              mediaStatus = 'paused';
            } else if (!previous) {
              mediaStatus = 'connecting';
            } else if (mediaType === 'audio') {
              // Opus DTX intentionally sends few/no packets during silence.
              // Once the receiver has obtained audio packets, do not call a
              // quiet microphone "stalled" unless the WebRTC track itself is
              // muted and packet delivery has also stopped.
              if (
                consumer.track?.muted &&
                wallNow - lastProgressAt >= 7000
              ) {
                mediaStatus = 'stalled';
              } else if (packetsReceived > 0 || bytes > 0) {
                mediaStatus = 'receiving';
              } else if (wallNow - lastProgressAt >= 7000) {
                mediaStatus = 'stalled';
              } else {
                mediaStatus = 'connecting';
              }
            } else if (wallNow - lastProgressAt >= 7000) {
              mediaStatus = 'stalled';
            }

            if (!result[sessionId]) {
              result[sessionId] = {
                bitrateKbps: 0,
                audioBitrateKbps: 0,
                videoBitrateKbps: 0,
                screenBitrateKbps: 0,
                packetLoss: null,
                rttMs: null,
                jitterMs: null,
                isOnline: true,
                media: {},
                sampledAt: wallNow,
                _hasBitrateSample: false,
                _hasPacketSample: false,
                _receivedDelta: 0,
                _lostDelta: 0,
              };
            }
            const session = result[sessionId];
            const roundedBitrate = Math.round(Math.max(0, bitrateKbps));
            if (hasBitrateSample) {
              session._hasBitrateSample = true;
              session.bitrateKbps += roundedBitrate;
              if (mediaType === 'audio') {
                session.audioBitrateKbps += roundedBitrate;
              }
              if (mediaType === 'video' || mediaType === 'screen') {
                session.videoBitrateKbps += roundedBitrate;
              }
              if (mediaType === 'screen') {
                session.screenBitrateKbps += roundedBitrate;
              }
            }
            if (hasPacketSample) {
              session._hasPacketSample = true;
              session._receivedDelta += receivedDelta;
              session._lostDelta += lostDelta;
            }
            if (Number.isFinite(rttMs)) {
              session.rttMs = Math.max(session.rttMs || 0, Math.round(rttMs));
            }
            if (Number.isFinite(jitterMs)) {
              session.jitterMs = Math.max(
                session.jitterMs || 0,
                Math.round(jitterMs)
              );
            }
            session.isOnline =
              session.isOnline &&
              (typeof navigator === 'undefined' || navigator.onLine) &&
              !['failed', 'closed'].includes(transportState);
            session.connectionState = transportState;
            session.media[mediaType] = {
              status: mediaStatus,
              bitrateKbps: hasBitrateSample ? roundedBitrate : null,
              packetLoss: hasPacketSample
                ? Number(packetLoss.toFixed(1))
                : null,
              frameWidth:
                frameWidth > 0 ? Math.round(frameWidth) : null,
              frameHeight:
                frameHeight > 0 ? Math.round(frameHeight) : null,
              framesPerSecond:
                framesPerSecond == null
                  ? null
                  : Number(Math.max(0, framesPerSecond).toFixed(1)),
              trackMuted: Boolean(consumer.track?.muted),
              lastProgressAt,
            };
          } catch {
            // Preserve all other participants when one getStats() call fails.
          }
        }
      )
    );

    for (const session of Object.values(result)) {
      if (!session._hasBitrateSample) {
        session.bitrateKbps = null;
        session.audioBitrateKbps = null;
        session.videoBitrateKbps = null;
        session.screenBitrateKbps = null;
      }
      const packetTotal = session._receivedDelta + session._lostDelta;
      session.packetLoss =
        session._hasPacketSample && packetTotal > 0
          ? Number(
              Math.min(
                100,
                (session._lostDelta / packetTotal) * 100
              ).toFixed(1)
            )
          : null;
      delete session._hasBitrateSample;
      delete session._hasPacketSample;
      delete session._receivedDelta;
      delete session._lostDelta;
    }

    return result;
  }

  async getUplinkKbps() {
    let totalBytes = 0;

    for (const producer of this.producers.values()) {
      try {
        const stats = await producer.getStats();
        stats.forEach((r) => {
          if (r.type === 'outbound-rtp' && !r.isRemote && typeof r.bytesSent === 'number') {
            totalBytes += r.bytesSent;
          }
        });
      } catch (_) {}
    }

    if (this.screenProducer) {
      try {
        const stats = await this.screenProducer.getStats();
        stats.forEach((r) => {
          if (r.type === 'outbound-rtp' && !r.isRemote && typeof r.bytesSent === 'number') {
            totalBytes += r.bytesSent;
          }
        });
      } catch (_) {}
    }

    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const dtMs = Math.max(1, now - (this._lastTx.ts || now));         
    const dBytes = Math.max(0, totalBytes - (this._lastTx.bytes || 0)); 
    this._lastTx = { bytes: totalBytes, ts: now };

    return (dBytes * 8) / dtMs;
  }

  async getTransportStats() {
    const results = { send: { bitrate: 0, packetsSent: 0 }, recv: { bitrate: 0, packetsReceived: 0 } };
    try {
      if (this.sendTransport && !this.sendTransport.closed) {
        const sendStats = await this.sendTransport.getStats();
        if (sendStats && sendStats.length > 0) {
          results.send = {
            bitrate: sendStats[0].bitrate || 0,
            packetsSent: sendStats[0].packetsSent || 0
          };
        }
      }
      if (this.recvTransport && !this.recvTransport.closed) {
        const recvStats = await this.recvTransport.getStats();
        if (recvStats && recvStats.length > 0) {
          results.recv = {
            bitrate: recvStats[0].bitrate || 0,
            packetsReceived: recvStats[0].packetsReceived || 0
          };
        }
      }
      return results;
    } catch (error) {
      console.error('Error getting transport stats:', error);
      return results;
    }
  }

  async getNetworkReceiveSpeed() {
    const out = {};
    const now = performance.now();

    const entries = Array.from(this.activeConsumers.entries());
    await Promise.all(
      entries.map(async ([producerId, consumer]) => {
        try {
          const stats = await consumer.getStats();
          let bytes = 0;

          stats.forEach((rep) => {
            if (rep.type === 'inbound-rtp' && !rep.isRemote) {
              bytes += rep.bytesReceived || 0;
            }
          });

          const key = consumer.id || producerId;
          const last = this._bytesCache.get(key);
          let mbps = 0;

          if (last && bytes >= last.bytes) {
            const dBytes = bytes - last.bytes;
            const dMs = Math.max(1, now - last.ts);
            const kbps = (dBytes * 8) / dMs; 
            mbps = kbps / 1000;
          }

          this._bytesCache.set(key, { ts: now, bytes });

          const sessionId = this.producerSessionMap.get(producerId);
          if (!sessionId) return;
          out[sessionId] = (out[sessionId] || 0) + mbps;
        } catch {
          /* ignore */
        }
      })
    );

    return out;
  }

  async replaceTrack(kind, newTrack) {
    try {
      const producer = this.producers.get(kind);
      if (!producer) throw new Error(`No ${kind} producer found`);
      const wasPaused =
        producer.paused || this._mediaEnabled[kind] !== true;
      await producer.replaceTrack({ track: newTrack });
      newTrack.enabled = !wasPaused;
      if (!wasPaused && producer.paused) await producer.resume();

      return true;
    } catch (error) {
      console.error(`Error replacing ${kind} track:`, error);
      throw error;
    }
  }

  async handleTrackEnded(kind) {
    if (this._destroyed || this._mediaEnabled[kind] !== true) return false;
    if (this._trackRecoveryPromises.has(kind)) {
      return this._trackRecoveryPromises.get(kind);
    }
    console.warn(`${kind} track ended, attempting to recover...`);
    const recovery = this._queueMediaOperation(kind, async (assertCurrent) => {
      try {
        if (kind === 'audio') {
          await this._reacquireAudioTrack();
        } else if (kind === 'video') {
          await this._reacquireVideoTrack();
        }
        assertCurrent();
        const producer = this.producers.get(kind);
        const track = this.localStream?.getTracks?.().find(
          (item) => item.kind === kind && item.readyState === 'live'
        );
        const shouldRemainEnabled = this._mediaEnabled[kind] === true;
        if (!shouldRemainEnabled) {
          if (track) track.enabled = false;
          if (kind === 'audio' && this._micInputTrack) {
            this._micInputTrack.enabled = false;
          }
          if (producer && !producer.closed && !producer.paused) {
            await producer.pause();
            assertCurrent();
          }
        } else if (track && (!producer || producer.closed)) {
          await this.produceTrack({
            track,
            kind,
            appData: kind === 'audio'
              ? getMeetingAudioProducerAppData(this._audioMode, {
                  processed: true,
                })
              : {},
          });
          assertCurrent();
        } else if (producer?.paused) {
          await producer.resume();
          assertCurrent();
        }
        assertCurrent();
        this._notifyLocalMediaState(kind, {
          reason: 'track-recovered',
        });
        return true;
      } catch (error) {
        if (error?.code === 'STALE_MEDIA_OPERATION') return false;
        console.error(`Failed to recover ${kind} track:`, error);
        await this._handleLocalTrackRecoveryFailure(kind, error);
        return false;
      }
    }).finally(() => {
      if (this._trackRecoveryPromises.get(kind) === recovery) {
        this._trackRecoveryPromises.delete(kind);
      }
    });
    this._trackRecoveryPromises.set(kind, recovery);
    return recovery;
  }

  async _handleLocalTrackRecoveryFailure(kind, error) {
    if (!['audio', 'video'].includes(kind)) return;
    this._mediaEnabled[kind] = false;
    const track = this.localStream?.getTracks?.().find(
      (item) => item.kind === kind
    );
    if (track) track.enabled = false;
    if (kind === 'audio' && this._micInputTrack) {
      this._micInputTrack.enabled = false;
    }
    const producer = this.producers.get(kind);
    if (producer && !producer.closed && !producer.paused) {
      await producer.pause().catch(() => undefined);
    }
    this._notifyLocalMediaState(kind, {
      reason: 'track-unavailable',
      errorName: String(error?.name || error?.code || ''),
      errorMessage: String(error?.message || ''),
    });
  }

  async ensureLocalMedia() {
    if (this._destroyed || !this.localStream) return;
    for (const kind of ['audio', 'video']) {
      if (!this.expectedMediaKinds.has(kind)) continue;
      if (this._mediaEnabled[kind] !== true) continue;
      try {
        await this._queueMediaOperation(
          kind,
          (assertCurrent) =>
            this._ensureLocalMediaKind(kind, assertCurrent)
        );
      } catch (error) {
        // One unavailable device must not tear down the other device or the
        // receive transport. Controls can retry it later.
        console.warn(`Unable to ensure local ${kind}:`, error?.message || error);
      }
    }
  }

  async _ensureLocalMediaKind(
    kind,
    assertCurrent = () => undefined
  ) {
    // Health recovery follows the user's current mute intent. It never opens
    // or resumes a muted microphone merely to repair its speaking indicator.
    if (this._mediaEnabled[kind] !== true) return;
    if (kind === 'audio') {
      await this._ensureMicrophoneProcessingRunning(assertCurrent);
      assertCurrent();
    }
    let track = this.localStream?.getTracks?.().find(
      (item) => item.kind === kind
    );
    const microphoneInputEnded =
      kind === 'audio' &&
      (!this._micInputTrack || this._micInputTrack.readyState !== 'live' ||
        this._micInputTrack.muted === true);
    if (!track || track.readyState !== 'live' || microphoneInputEnded) {
      track = kind === 'audio'
        ? await this._reacquireAudioTrack()
        : await this._reacquireVideoTrack();
      assertCurrent();
    }
    if (this._mediaEnabled[kind] !== true || track?.readyState !== 'live') return;
    track.enabled = true;
    if (kind === 'audio' && this._micInputTrack) this._micInputTrack.enabled = true;
    let producer = this.producers.get(kind);
    if (!producer || producer.closed) {
      producer = await this.produceTrack({
        track, kind,
        appData: kind === 'audio'
          ? getMeetingAudioProducerAppData(this._audioMode, {
              processed: track !== this._micInputTrack,
            }) : {},
      });
      assertCurrent();
    } else if (producer.track?.id !== track.id) {
      await producer.replaceTrack({ track });
      assertCurrent();
    }
    if (producer?.paused && this._mediaEnabled[kind] === true) {
      await producer.resume();
      assertCurrent();
    }
  }

  _hasOutboundMediaIntent() {
    const hasLocalProducer = Array.from(this.producers.values()).some(
      (producer) => producer && !producer.closed
    );
    const hasEnabledLocalKind = ['audio', 'video'].some(
      (kind) => this._mediaEnabled[kind] === true
    );
    const hasLiveScreen = Boolean(
      (this.screenProducer && !this.screenProducer.closed) ||
      this.screenStream?.getVideoTracks?.().some(
        (track) => track.readyState === 'live'
      ) ||
      this._screenShareStartPromise
    );
    return hasLocalProducer || hasEnabledLocalKind || hasLiveScreen;
  }

  async ensureMediaHealth() {
    if (this._destroyed || (typeof navigator !== 'undefined' && !navigator.onLine)) {
      return false;
    }
    if (!this._signalingReady) return false;

    const sendState = this.sendTransport?.connectionState;
    const recvState = this.recvTransport?.connectionState;
    // Do not recreate a send transport just because an admitted participant
    // joined with both devices off. A transport without an outgoing track
    // never starts DTLS and is intentionally removed by the server's orphan
    // timer. The first real producer creates it lazily through produceTrack().
    if (this._hasOutboundMediaIntent() &&
        (!this.sendTransport || this.sendTransport.closed ||
        ['failed', 'disconnected'].includes(sendState))) {
      await this.handleTransportFailure('send', {
        forceRebuild: sendState === 'failed',
      });
    }
    if (!this.recvTransport || this.recvTransport.closed ||
        ['failed', 'disconnected'].includes(recvState)) {
      await this.handleTransportFailure('recv', {
        forceRebuild: recvState === 'failed',
      });
    }
    await this.ensureLocalMedia();
    return true;
  }

  async recoverAfterSignalingReconnect({ socket, roomId, userId, sessionId } = {}) {
    if (this._destroyed) return false;
    this.socket = socket || this.socket;
    this.roomId = roomId || this.roomId;
    this.userId = userId || this.userId;
    this.sessionId = String(sessionId || this.getMeetingSessionId() || '').trim();
    this.setSignalingReady(true);

    // A Socket.IO reconnect does not imply that DTLS/ICE failed. Keep healthy
    // WebRTC transports (and uninterrupted audio/video) alive; recover only the
    // path whose own connection state says it needs work.
    const sendState = this.sendTransport?.connectionState;
    const recvState = this.recvTransport?.connectionState;
    if (
      this._hasOutboundMediaIntent() &&
      (!this.sendTransport ||
        this.sendTransport.closed ||
        ['failed', 'disconnected', 'closed'].includes(sendState))
    ) {
      await this.handleTransportFailure('send', {
        forceRebuild:
          !this.sendTransport ||
          this.sendTransport.closed ||
          ['failed', 'closed'].includes(sendState),
      });
    }
    if (
      !this.recvTransport ||
      this.recvTransport.closed ||
      ['failed', 'disconnected', 'closed'].includes(recvState)
    ) {
      await this.handleTransportFailure('recv', {
        forceRebuild:
          !this.recvTransport ||
          this.recvTransport.closed ||
          ['failed', 'closed'].includes(recvState),
      });
    }
    await this.ensureLocalMedia();
    return true;
  }

  async reinitialize({ socket, roomId, userId }) {
    this._destroyed = false;
    this.stopAdaptiveController();

    this.socket = socket || this.socket;
    this.roomId = roomId || this.roomId;
    this.userId = userId || this.userId;
    this.sessionId = this.getMeetingSessionId();

    const sendTransportId = this.sendTransport?.id;
    const recvTransportId = this.recvTransport?.id;
    this.cleanupStreams();
    await Promise.allSettled([
      this.closeServerTransport(sendTransportId),
      this.closeServerTransport(recvTransportId),
    ]);
    try { this.sendTransport?.close(); } catch {}
    try { this.recvTransport?.close(); } catch {}

    this.sendTransport = null;
    this.recvTransport = null;
    this.producerSessionMap.clear();
    this._bytesCache.clear();
    this._networkStatsCache.clear();
    this._localStatsCache.clear();
    this.consumerStatsMap.clear();
    this.consumePromises.clear();
    this._consumeQueue = Promise.resolve();
    this._recvCreatePromise = null;
    this._sendCreatePromise = null;
    this._adaptiveStatsCache = null;
    this._availableOutgoingKbpsEwma = null;
    this._autoUpgradeSamples = 0;
    this._appliedVideoProfileKey = null;
    this._appliedVideoProducerId = '';
    this._appliedVideoTrackId = '';
    this.device = new Device();
  }

  closeConsumersBySession(sessionId) {
    for (const [producerId] of [...this.activeConsumers.entries()]) {
      const sid = this.producerSessionMap.get(producerId);
      if (sid === sessionId) {
        void this.closeConsumerByProducer(producerId);
      }
    }
  }

  _queueVideoQualityOperation(operation) {
    const queued = this._videoQualityChangePromise.then(
      operation,
      operation
    );
    this._videoQualityChangePromise = queued.catch(() => undefined);
    return queued;
  }

  _reapplyCurrentCameraQuality() {
    return this._queueVideoQualityOperation(async () => {
      const target =
        this._videoQualityMode === 'auto'
          ? this._appliedVideoProfileKey || 'medium'
          : this._videoQualityMode;
      try {
        return await this._applyCameraVideoProfile(target);
      } catch (error) {
        console.warn(
          'Camera quality could not be restored after changing the camera:',
          error?.message || error
        );
        this._emitVideoQualityState({
          error:
            error?.message ||
            'The selected camera could not apply this quality.',
        });
        return this.getVideoQualityStatus();
      }
    });
  }

  getVideoQualityStatus() {
    const producer = this.producers.get('video');
    const track = this.localStream?.getVideoTracks?.()[0];
    return {
      mode: this._videoQualityMode,
      appliedProfile: this._appliedVideoProfileKey,
      pending: !producer || producer.closed || track?.readyState !== 'live',
    };
  }

  _emitVideoQualityState(extra = {}) {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
      new CustomEvent('meeting-video-quality-change', {
        detail: {
          ...this.getVideoQualityStatus(),
          ...extra,
        },
      })
    );
  }

  async _setCameraEncodingBudget(producer, maxBitrate) {
    const sender = producer?.rtpSender;
    const safeBudget = Math.max(90_000, Number(maxBitrate) || 0);
    if (sender?.getParameters && sender?.setParameters) {
      const parameters = sender.getParameters();
      const encodings = Array.isArray(parameters?.encodings)
        ? parameters.encodings
        : [];
      if (encodings.length > 0) {
        const weights =
          encodings.length === 1
            ? [1]
            : encodings.length === 2
              ? [0.3, 0.7]
              : [0.1, 0.25, 0.65];
        parameters.encodings = encodings.map((encoding, index) => ({
          ...encoding,
          maxBitrate: Math.max(
            30_000,
            Math.round(
              safeBudget *
                (weights[index] || 1 / Math.max(1, encodings.length))
            )
          ),
        }));
        await sender.setParameters(parameters);
        return;
      }
    }

    if (producer?.setRtpEncodingParameters) {
      // mediasoup applies this value to every simulcast encoding. Divide the
      // total budget so it cannot accidentally become a 3× aggregate cap.
      await producer.setRtpEncodingParameters({
        maxBitrate: Math.max(30_000, Math.round(safeBudget / 3)),
      });
    }
  }

  async _applyCameraVideoProfile(profileKey) {
    const key = VIDEO_QUALITY_PROFILES[profileKey] ? profileKey : 'medium';
    const producer = this.producers.get('video');
    const track = this.localStream?.getVideoTracks?.()[0];
    if (
      !producer ||
      producer.closed ||
      !track ||
      track.readyState !== 'live'
    ) {
      this._emitVideoQualityState({ pending: true });
      return this.getVideoQualityStatus();
    }
    if (
      this._appliedVideoProfileKey === key &&
      this._appliedVideoProducerId === String(producer.id || '') &&
      this._appliedVideoTrackId === String(track.id || '')
    ) {
      return this.getVideoQualityStatus();
    }
    const profile = VIDEO_QUALITY_PROFILES[key];

    const previousProfileKey = this._appliedVideoProfileKey;
    const previousProducerId = this._appliedVideoProducerId;
    const previousTrackId = this._appliedVideoTrackId;
    const previousConstraints =
      typeof track.getConstraints === 'function'
        ? track.getConstraints()
        : {};
    const previousEnabled = track.enabled;
    const previousPaused = Boolean(producer.paused);
    let previousEncodings = null;
    try {
      const parameters = producer.rtpSender?.getParameters?.();
      previousEncodings = Array.isArray(parameters?.encodings)
        ? parameters.encodings.map((encoding) => ({ ...encoding }))
        : null;
    } catch {
      previousEncodings = null;
    }

    try {
      await track.applyConstraints({
        width: { ideal: profile.width, max: profile.width },
        height: { ideal: profile.height, max: profile.height },
        frameRate: {
          ideal: profile.frameRate,
          max: profile.frameRate,
        },
      });
      await this._setCameraEncodingBudget(producer, profile.maxBitrate);
      if (producer.setMaxSpatialLayer) {
        await producer.setMaxSpatialLayer(profile.maxSpatialLayer);
      }
      track.enabled = previousEnabled;
      if (previousPaused && !producer.paused) await producer.pause();
      if (!previousPaused && producer.paused) await producer.resume();
      this._appliedVideoProfileKey = key;
      this._appliedVideoProducerId = String(producer.id || '');
      this._appliedVideoTrackId = String(track.id || '');
      this._emitVideoQualityState();
      return this.getVideoQualityStatus();
    } catch (error) {
      try {
        if (
          previousConstraints &&
          Object.keys(previousConstraints).length > 0
        ) {
          await track.applyConstraints(previousConstraints);
        }
      } catch {}
      try {
        if (
          previousEncodings &&
          producer.rtpSender?.getParameters &&
          producer.rtpSender?.setParameters
        ) {
          const rollback = producer.rtpSender.getParameters();
          rollback.encodings = previousEncodings;
          await producer.rtpSender.setParameters(rollback);
        }
      } catch {}
      try {
        if (
          previousProfileKey &&
          producer.setMaxSpatialLayer
        ) {
          await producer.setMaxSpatialLayer(
            VIDEO_QUALITY_PROFILES[previousProfileKey].maxSpatialLayer
          );
        }
      } catch {}
      track.enabled = previousEnabled;
      try {
        if (previousPaused && !producer.paused) await producer.pause();
        if (!previousPaused && producer.paused) await producer.resume();
      } catch {}
      this._appliedVideoProfileKey = previousProfileKey;
      this._appliedVideoProducerId = previousProducerId;
      this._appliedVideoTrackId = previousTrackId;
      throw error;
    }
  }

  async setVideoQualityMode(value) {
    const requestedMode = normalizeVideoQualityMode(value);
    return this._queueVideoQualityOperation(async () => {
      const previousMode = this._videoQualityMode;
      this.stopAdaptiveController();
      this._videoQualityMode = requestedMode;
      const target =
        requestedMode === 'auto'
          ? this._appliedVideoProfileKey || 'medium'
          : requestedMode;
      try {
        await this._applyCameraVideoProfile(target);
        localStorage.setItem(VIDEO_QUALITY_STORAGE_KEY, requestedMode);
        this._emitVideoQualityState();
        if (requestedMode === 'auto') this.startAdaptiveController();
        return this.getVideoQualityStatus();
      } catch (error) {
        this._videoQualityMode = previousMode;
        if (previousMode === 'auto') this.startAdaptiveController();
        this._emitVideoQualityState({
          error: error?.message || 'Unable to apply camera quality.',
        });
        throw error;
      }
    });
  }

  async _collectAdaptiveVideoStats() {
    const producer = this.producers.get('video');
    if (!producer || producer.closed) return null;
    const now =
      typeof performance !== 'undefined' ? performance.now() : Date.now();
    let bytesSent = 0;
    let packetsSent = 0;
    let packetsLost = 0;
    let remoteReportMarker = '';
    let qualityLimitationReason = '';
    let availableOutgoingKbps = null;
    let rttMs = null;

    const [producerStats, transportStats] = await Promise.all([
      producer.getStats(),
      this.sendTransport?.getStats?.().catch?.(() => null) || null,
    ]);
    producerStats?.forEach?.((report) => {
      if (report.type === 'outbound-rtp' && !report.isRemote) {
        bytesSent += Number(report.bytesSent || 0);
        packetsSent += Number(report.packetsSent || 0);
        if (report.qualityLimitationReason) {
          qualityLimitationReason = String(
            report.qualityLimitationReason
          ).toLowerCase();
        }
      }
      if (report.type === 'remote-inbound-rtp') {
        packetsLost += Math.max(0, Number(report.packetsLost || 0));
        const measurements = Number(report.roundTripTimeMeasurements || 0);
        const totalRtt = Number(report.totalRoundTripTime || 0);
        remoteReportMarker += `${measurements}:${totalRtt}:${Number(
          report.packetsLost || 0
        )}|`;
        if (Number.isFinite(report.roundTripTime)) {
          rttMs = Math.max(rttMs || 0, report.roundTripTime * 1000);
        }
      }
    });
    transportStats?.forEach?.((report) => {
      if (
        report.type === 'candidate-pair' &&
        (report.nominated || report.selected)
      ) {
        if (Number.isFinite(report.availableOutgoingBitrate)) {
          availableOutgoingKbps = Math.max(
            availableOutgoingKbps || 0,
            report.availableOutgoingBitrate / 1000
          );
        }
        if (Number.isFinite(report.currentRoundTripTime)) {
          rttMs = Math.max(rttMs || 0, report.currentRoundTripTime * 1000);
        }
      }
    });

    const previous = this._adaptiveStatsCache;
    let achievedVideoKbps = null;
    let packetLoss = null;
    let sentAtLastRemoteReport =
      previous?.sentAtLastRemoteReport ?? packetsSent;
    let lostAtLastRemoteReport =
      previous?.lostAtLastRemoteReport ?? packetsLost;
    if (previous && bytesSent >= previous.bytesSent) {
      const elapsedMs = Math.max(1, now - previous.timestamp);
      achievedVideoKbps = ((bytesSent - previous.bytesSent) * 8) / elapsedMs;
    }
    if (
      previous &&
      remoteReportMarker &&
      remoteReportMarker !== previous.remoteReportMarker
    ) {
      const sentDelta = Math.max(
        0,
        packetsSent - previous.sentAtLastRemoteReport
      );
      const lostDelta = Math.max(
        0,
        packetsLost - previous.lostAtLastRemoteReport
      );
      if (sentDelta > 0) {
        packetLoss = Math.min(100, (lostDelta / sentDelta) * 100);
      }
      sentAtLastRemoteReport = packetsSent;
      lostAtLastRemoteReport = packetsLost;
    }
    this._adaptiveStatsCache = {
      timestamp: now,
      bytesSent,
      remoteReportMarker,
      sentAtLastRemoteReport,
      lostAtLastRemoteReport,
    };

    if (availableOutgoingKbps != null) {
      this._availableOutgoingKbpsEwma =
        this._availableOutgoingKbpsEwma == null
          ? availableOutgoingKbps
          : this._availableOutgoingKbpsEwma * 0.7 +
            availableOutgoingKbps * 0.3;
    }

    return {
      availableOutgoingKbps: this._availableOutgoingKbpsEwma,
      achievedVideoKbps,
      packetLoss,
      rttMs,
      qualityLimitationReason,
    };
  }

  async _runAdaptiveVideoTick(generation) {
    return this._queueVideoQualityOperation(async () => {
      if (
        this._destroyed ||
        !this._adaptControllerActive ||
        generation !== this._adaptGeneration ||
        this._videoQualityMode !== 'auto'
      ) {
        return;
      }
      const stats = await this._collectAdaptiveVideoStats();
      if (
        !stats ||
        generation !== this._adaptGeneration ||
        this._videoQualityMode !== 'auto'
      ) {
        return;
      }
      const current = this._appliedVideoProfileKey || 'medium';
      const availableForCamera =
        stats.availableOutgoingKbps != null && this.screenProducer
          ? stats.availableOutgoingKbps * 0.55
          : stats.availableOutgoingKbps;
      const candidate = chooseAutomaticVideoProfile({
        ...stats,
        availableOutgoingKbps: availableForCamera,
        fallback: current,
      });
      const currentIndex = VIDEO_QUALITY_ORDER.indexOf(current);
      const candidateIndex = VIDEO_QUALITY_ORDER.indexOf(candidate);
      if (candidateIndex < currentIndex) {
        this._autoUpgradeSamples = 0;
        await this._applyCameraVideoProfile(candidate);
      } else if (candidateIndex > currentIndex) {
        this._autoUpgradeSamples += 1;
        if (this._autoUpgradeSamples >= 3) {
          this._autoUpgradeSamples = 0;
          await this._applyCameraVideoProfile(
            VIDEO_QUALITY_ORDER[Math.min(candidateIndex, currentIndex + 1)]
          );
        }
      } else {
        this._autoUpgradeSamples = 0;
      }
    });
  }

  startAdaptiveController() {
    if (
      this._destroyed ||
      this._videoQualityMode !== 'auto' ||
      this._adaptControllerActive
    ) {
      return;
    }
    this._adaptControllerActive = true;
    const generation = ++this._adaptGeneration;
    const tick = async () => {
      if (
        !this._adaptControllerActive ||
        generation !== this._adaptGeneration
      ) {
        return;
      }
      try {
        await this._runAdaptiveVideoTick(generation);
      } catch (error) {
        console.warn(
          'Automatic camera quality update failed:',
          error?.message || error
        );
      } finally {
        if (
          this._adaptControllerActive &&
          generation === this._adaptGeneration
        ) {
          this._adaptTimer = window.setTimeout(tick, 2500);
        }
      }
    };
    void tick();
  }

  stopAdaptiveController() {
    this._adaptControllerActive = false;
    this._adaptGeneration += 1;
    this._autoUpgradeSamples = 0;
    if (this._adaptTimer) {
      window.clearTimeout(this._adaptTimer);
      this._adaptTimer = null;
    }
  }

  cleanupStreams() {
    this._mediaLifecycleGeneration += 1;
    this._recvGeneration += 1;
    this._mediaOperationGeneration.audio += 1;
    this._mediaOperationGeneration.video += 1;
    this.stopDeviceMonitor();
    this._trackMuteRecoveryTimers.forEach(({ timer }) =>
      window.clearTimeout(timer)
    );
    this._trackMuteRecoveryTimers.clear();
    this._legacySendRetryTimers.forEach((timer) => window.clearTimeout(timer));
    this._legacySendRetryTimers.clear();
    if (this._legacySendHandover) {
      this._legacySendHandover.producers?.forEach((producer) => {
        try { producer?.close(); } catch {}
      });
      try { this._legacySendHandover.screenProducer?.close(); } catch {}
      void this.closeServerTransport(
        this._legacySendHandover.transport?.id
      ).catch(() => undefined);
      try { this._legacySendHandover.transport?.close(); } catch {}
      this._legacySendHandover = null;
    }
    this.localStream?.getTracks().forEach((track) => {
      track.onended = null;
      track.stop();
    });
    this.localStream = null;
    this._sourceJoinStream = null;
    this._disposeMicProcessing();

    this.screenStream?.getTracks().forEach((track) => {
      track.onended = null;
      track.stop();
    });
    this.screenStream = null;

    this.screenProducer?.close();
    this.screenProducer = null;
    this._notifyLocalScreenStream(null);

    this.producers.forEach(p => p.close());
    this.producers.clear();
    this._appliedVideoProducerId = '';
    this._appliedVideoTrackId = '';

    this.activeConsumers.forEach(c => c.close());
    this.activeConsumers.clear();
    this.consumePromises.clear();
    this._trackRecoveryPromises.clear();
  }

  destroy() {
    if (this._destroyed) return;
    this._destroyed = true;
    this.stopAdaptiveController();
    this.cleanupStreams();
    for (const p of this.producers.values()) { try { p.close(); } catch {} }
    for (const c of this.activeConsumers.values()) { try { c.close(); } catch {} }
    this.producerSessionMap.clear();
    this._bytesCache.clear();
    this._networkStatsCache.clear();
    this._localStatsCache.clear();
    const sendTransportId = this.sendTransport?.id;
    const recvTransportId = this.recvTransport?.id;
    void this.closeServerTransport(sendTransportId).catch(() => undefined);
    void this.closeServerTransport(recvTransportId).catch(() => undefined);
    try { this.sendTransport?.close(); } catch {}
    try { this.recvTransport?.close(); } catch {}
    this.sendTransport = null;
    this.recvTransport = null;
    this.device = null;
    this.producers.clear();
    this.activeConsumers.clear();
    this.consumePromises.clear();
    this._consumeQueue = Promise.resolve();
    this._recvCreatePromise = null;
    this._sendCreatePromise = null;
    this._staleProducerIds.clear();
    this._trackRecoveryPromises.clear();
    this.producerSessionMap.clear();
    this._bytesCache.clear();
    this._networkStatsCache.clear();
    this._localStatsCache.clear();
  }

  async loadDevice(rtpCapabilities) {
    await this.initializeDevice(rtpCapabilities);
  }
}
